// ═══════════════════════════════════════════════════════════════════
// PRODUCT CATALOG — data-driven PDPs
// One PDP skeleton ([data-page="pdp"]) is populated by renderPDP()
// from KBD_PRODUCTS + KBD_PRODUCT_FAMILIES. Route pattern:
// navigate('pdp-[handle]'). Plain navigate('pdp') → hero product.
// Mirrors Shopify's product.liquid pattern.
// ═══════════════════════════════════════════════════════════════════

const KBD_CARE_DEFAULT = [
  'Wipe down with a damp cloth after each use',
  'Mild soap for deeper cleaning — never bleach or solvents',
  'Air dry away from direct sunlight',
  'Store in the included bag, away from heat',
  'Inspect hardware before each use'
];

const KBD_CARE_JACKET = [
  'Wipe down with a damp cloth after each use',
  'Mild soap for deeper cleaning — never bleach or solvents',
  'Air dry flat, away from direct sunlight',
  'Hang in a ventilated space between uses',
  'Check strapping and D-rings before each wear'
];

const KBD_CARE_IMPACT = [
  'Wipe leather with a damp cloth after each use',
  'Condition the PU leather with a small amount of leather conditioner quarterly',
  'Rattan cane: wipe clean, store dry — keep away from humidity',
  'Keep all pieces away from direct sunlight and heat',
  'Inspect stitching and handle rivets before each use'
];

const KBD_CARE_METAL = [
  'Wipe down with a damp cloth after each use',
  'Mild soap for deeper cleaning',
  "Dry the bar thoroughly — powder coating resists rust but doesn't eliminate it",
  'Inspect the integrated cuff stitching and buckles before each use',
  'Store flat in a dry place'
];

const KBD_CATEGORY_LABELS = {
  'restraint-sets': 'Restraint Kits',
  'jackets': 'Bondage Jackets',
  'impact-play': 'Impact Play',
  'complete-kits': 'Complete Kits'
};

const KBD_LEVEL_LABELS = {
  'beginner': 'New to play',
  'intermediate': 'Intermediate',
  'advanced': 'Advanced'
};

const KBD_EYEBROW_SINGULAR = {
  'restraint-sets': 'Restraint Kit',
  'jackets': 'Bondage Jacket',
  'impact-play': 'Impact Play',
  'complete-kits': 'Complete Kit'
};

const KBD_IMAGE_FLAT = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80';
const KBD_IMAGE_DETAIL = 'https://images.unsplash.com/photo-1558618047-f4e90e8de0cc?w=800&q=80';
const KBD_IMAGE_EDITORIAL = 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=1200&q=80';
const KBD_IMAGE_HERO = 'https://images.unsplash.com/photo-1550684376-efef52f78b22?w=1600&q=80';

const KBD_PRODUCT_FAMILIES = {

  'bed-restraint-5pc': {
    breadcrumbCategory: 'Restraint Kits',
    titleHead: 'The Bed Restraint',
    titleEm: '5-Piece Kit',
    mainImage: KBD_IMAGE_FLAT,
    thumbImages: [KBD_IMAGE_FLAT, KBD_IMAGE_DETAIL, KBD_IMAGE_EDITORIAL, KBD_IMAGE_HERO],
    description: "A minimal, bed-ready kit for couples starting out. Six pieces — the ones you reach for first — in a form that stows back under the mattress when the scene is over. Everything you need to begin; nothing you don\u2019t.",
    includedList: [
      '2 × wrist cuffs (padded PU leather, adjustable 5–9" circumference)',
      '2 × ankle cuffs (padded PU leather, adjustable 7–12" circumference)',
      '1 × leather blindfold with elastic strap',
      '1 × silicone ball gag with nickel-free buckle',
      '1 × short whip (40cm)',
      '1 × pair of adjustable nipple clamps with chain'
    ],
    materialsHtml: '<p><strong>Primary material:</strong> Leathery PU, polyester lining<br>'
      + '<strong>Hardware:</strong> Nickel-free alloy, matte finish<br>'
      + '<strong>Blindfold:</strong> Leather outer, soft fabric inner, elastic strap<br>'
      + '<strong>Cuff adjustment:</strong> Wrist 5–9", ankle 7–12"<br>'
      + '<strong>Whip:</strong> 40cm, PU leather falls<br>'
      + '<strong>Weight:</strong> 0.9 lbs (packaged)</p>',
    piecesTitle: 'The Pieces.',
    piecesSub: 'Six items, grouped by function. No filler.',
    pieces: [
      { title: 'The Cuffs', body: "Two pairs — wrist and ankle, padded, adjustable. The starting vocabulary of restraint. If you\u2019ve never used cuffs before, these are forgiving: quick to release, soft against the skin, firm enough to feel real. Nickel-free hardware, reinforced stitching at every strap-to-ring junction." },
      { title: 'The Blindfold', body: "Leather outer, soft fabric lining, elastic strap. The fastest way to shift the temperature of a scene. Sits comfortably against the temples without pressure, and removes instantly when needed — a detail that matters more than it sounds." },
      { title: 'The Gag', body: "Silicone ball, nickel-free buckle. Use it lightly or not at all — it belongs in the kit because once it\u2019s on the nightstand, it becomes a choice you can make. Quick-release buckle on the reverse; easy to undo one-handed." },
      { title: 'The Whip & Clamps', body: "A short whip for light impact and a pair of adjustable nipple clamps linked with chain. The first, quiet steps into sensation play. Low-stakes, easy to put down, easy to pick up again." }
    ],
    relatedFamilies: ['standard-10pc', '15pc-set', 'luxury-8pc']
  },

  'standard-10pc': {
    breadcrumbCategory: 'Restraint Kits',
    titleHead: 'The 10-Piece',
    titleEm: 'Standard Kit',
    mainImage: KBD_IMAGE_FLAT,
    thumbImages: [KBD_IMAGE_FLAT, KBD_IMAGE_DETAIL, KBD_IMAGE_EDITORIAL, KBD_IMAGE_HERO, KBD_IMAGE_FLAT],
    description: "A complete starting set built for couples beginning to explore restraint together. Ten pieces curated to work as a whole — softer materials, forgiving adjustments, and nothing included that doesn\u2019t earn its place.",
    includedList: [
      '1 × collar with attached leash',
      '2 × wrist cuffs (adjustable, 5–9" circumference)',
      '2 × ankle cuffs (adjustable, 7–12" circumference)',
      '1 × connecting cross / under-bed restraint',
      '1 × padded blindfold',
      '1 × ball gag (silicone, nickel-free buckle)',
      '1 × short whip',
      '1 × feather tickler',
      '1 × pair of adjustable nipple clamps with chain',
      '5 meters of soft bondage rope'
    ],
    materialsHtml: '<p><strong>Primary material:</strong> Leathery PU, lined with soft polyester against the skin<br>'
      + '<strong>Hardware:</strong> Nickel-free alloy, matte finish<br>'
      + '<strong>Rope:</strong> Soft polyester, 5m, approximately 8mm diameter<br>'
      + '<strong>Cuff adjustment:</strong> Wrist 5–9", ankle 7–12"<br>'
      + '<strong>Collar adjustment:</strong> 12–16"<br>'
      + '<strong>Weight:</strong> 1.2 lbs (packaged)</p>',
    piecesTitle: 'The Pieces.',
    piecesSub: "Every item in the set, and why it\u2019s here.",
    pieces: [
      { title: 'The Collar', body: "A starting point for every scene. Padded PU leather with nickel-free hardware, adjustable from 12 to 16 inches. The attached D-ring accepts the included leash or any standard clip. A good collar sets the tone — this one is soft, secure, and deliberately neutral so it works as the anchor of your practice." },
      { title: 'The Wrist & Ankle Cuffs', body: "Padded lining, buckle closure, adjustable from 5 to 9 inches at the wrist and 7 to 12 at the ankle — sized to fit a wide range of bodies. Each cuff has a D-ring that works with the rope, the cross, or any standard clip. Stitching is reinforced at the strap-to-ring junction. The pair holds securely under tension without digging in." },
      { title: 'The Blindfold', body: "The quickest way to change a scene entirely. Three-layer construction — soft fabric against the skin, foam middle for light blocking, and an adjustable elastic strap that sits comfortably against the temples without pressure. Removes instantly when needed. A blindfold is often the first tool couples add, and for good reason." },
      { title: 'The Restraint Cross', body: "A central piece that connects all four cuffs and works under any mattress — no hardware installation, no marks, no trace. When the scene\u2019s over, it slides back into the included bag. This is the piece most couples find they use most often: it turns an everyday bed into a considered space." },
      { title: '5 Meters of Bondage Rope', body: "Soft polyester, 8mm diameter, finished ends — long enough to tie a basic chest harness or single-column tie, short enough to manage if you\u2019ve never used rope before. Safer than natural fibers for newcomers: no splinters, no itch, easier to untie in a hurry. Paired with our <em>Introduction to rope</em> guide in the Journal." },
      { title: 'Gag, Clamps & Feather', body: "The smaller instruments. A silicone ball gag with a nickel-free buckle, a pair of adjustable nipple clamps linked with chain, and a feather tickler for the moments you want to slow a scene down. These exist because a kit of just restraints is incomplete — sensation is what makes restraint feel like something." }
    ],
    relatedFamilies: ['15pc-set', 'bed-restraint-5pc', 'luxury-8pc']
  },

  '15pc-set': {
    breadcrumbCategory: 'Complete Kits',
    titleHead: 'The 15-Piece Kit',
    titleEm: 'with Bag',
    mainImage: KBD_IMAGE_FLAT,
    thumbImages: [KBD_IMAGE_FLAT, KBD_IMAGE_DETAIL, KBD_IMAGE_EDITORIAL, KBD_IMAGE_HERO, KBD_IMAGE_FLAT],
    description: "Everything in the The Standard (10 Piece), plus five additions and a carrying bag. The set for couples who already have an established practice and want more range within the same vocabulary.",
    includedList: [
      '1 × collar with attached leash',
      '2 × wrist cuffs (adjustable, 5–9" circumference)',
      '2 × ankle cuffs (adjustable, 7–12" circumference)',
      '1 × connecting cross / under-bed restraint',
      '1 × padded blindfold',
      '1 × ball gag (silicone, nickel-free buckle)',
      '1 × short whip',
      '1 × feather tickler',
      '1 × pair of adjustable nipple clamps with chain',
      '5 meters of soft bondage rope',
      '1 × firm PU leather paddle',
      '1 × silver toothed roller',
      '1 × silicone anal plug (medium, tapered)',
      '2 × paired prompt dice',
      '1 × matte fabric carrying bag (zip close, padded interior)'
    ],
    materialsHtml: '<p><strong>Primary material:</strong> Leathery PU, polyester lining<br>'
      + '<strong>Hardware:</strong> Nickel-free alloy, matte finish<br>'
      + '<strong>Rope:</strong> Soft polyester, 5m, 8mm diameter<br>'
      + '<strong>Paddle:</strong> Stitched-edge PU leather face, balanced wooden core<br>'
      + '<strong>Roller:</strong> Stainless steel teeth, weighted handle<br>'
      + '<strong>Plug:</strong> Medical-grade silicone, tapered, 4" insertable length<br>'
      + '<strong>Bag:</strong> Water-resistant fabric, 14" × 10" × 4", padded interior<br>'
      + '<strong>Weight:</strong> 2.4 lbs (packaged)</p>',
    piecesTitle: 'The Pieces.',
    piecesSub: 'Fifteen pieces, organised for range without overwhelm.',
    pieces: [
      { title: 'The Restraint Foundation', body: "Ten pieces of restraint and sensation — collar, cuffs, cross, blindfold, gag, whip, feather, clamps, rope. The considered starting vocabulary. If this is the set you\u2019re landing on first, every piece here is built to work together from the first scene." },
      { title: 'The Paddle', body: "Firm PU leather face, stitched edge, balanced grip. Harder than the short whip included with the basic set, gentler than a dedicated impact kit. The bridge between light and firm." },
      { title: 'The Silver Roller', body: "A weighted toothed roller. Drawn across skin, it produces a surprising range of sensation without leaving marks. Good as a warmup, good as a scene on its own. Stainless steel, cleans in seconds." },
      { title: 'The Anal Plug', body: "Silicone, medium size, tapered. Body-safe, non-porous, quick to clean. Included because a kit for an established practice often includes one. Use solo or with a partner." },
      { title: 'The Dice', body: "Paired prompt dice — one for body zones, one for acts. Used as a warmup or a starting point when a scene needs structure. Small, and easy to overlook, which is the point: they\u2019re there if you want them." },
      { title: 'The Carrying Bag', body: "Matte fabric with a zipper closure and padded interior. Discreet to keep on a shelf or in a bedside drawer. Organises every piece in the set. Nothing on the outside suggests what\u2019s inside." }
    ],
    relatedFamilies: ['standard-10pc', 'luxury-8pc', 'whip-set-8pc']
  },

  'luxury-8pc': {
    breadcrumbCategory: 'Restraint Kits',
    titleHead: 'The Luxury',
    titleEm: '8-Piece Set',
    mainImage: KBD_IMAGE_FLAT,
    thumbImages: [KBD_IMAGE_FLAT, KBD_IMAGE_DETAIL, KBD_IMAGE_EDITORIAL, KBD_IMAGE_HERO],
    description: "An eight-piece kit refined for couples past the basics. Fewer items than the Standard Kit, more considered in what\u2019s included — gold-finish hardware, rigid handcuffs instead of padded cuffs, a firm paddle instead of a feather. A shift in register, not in category.",
    includedList: [
      '1 × gold chain collar with quick-release clasp',
      '1 × pair of rigid metal handcuffs (double-locked, key included)',
      '2 × ankle cuffs (padded PU leather, adjustable 7–12")',
      '1 × leather blindfold (satin inner, elastic strap)',
      '1 × ball gag (silicone, nickel-free)',
      '1 × firm PU leather paddle',
      '1 × short whip',
      '1 × pair of adjustable chain nipple clamps'
    ],
    materialsHtml: '<p><strong>Primary material:</strong> Leathery PU, satin lining<br>'
      + '<strong>Hardware:</strong> Gold-finish alloy, nickel-free<br>'
      + '<strong>Handcuffs:</strong> Rigid steel, double-lock mechanism, quick-release key included<br>'
      + '<strong>Collar:</strong> Gold chain, 14–18" adjustable<br>'
      + '<strong>Paddle:</strong> Stitched-edge PU leather, 12" length<br>'
      + '<strong>Weight:</strong> 1.4 lbs (packaged)</p>',
    piecesTitle: 'The Pieces.',
    piecesSub: 'Eight pieces, refined for an established practice.',
    pieces: [
      { title: 'The Gold Chain Collar', body: "Not padded, not standard — a decorative chain collar with a quick-release clasp. For couples who\u2019ve moved from learning restraint to choosing its aesthetics. Not designed for long wear; designed for the moment it reads as intentional." },
      { title: 'The Handcuffs', body: "Rigid metal, double-locked, with a quick-release key. A different kind of restraint than padded cuffs — unforgiving, decisive, faster to apply. Included because there\u2019s a point in any practice where soft isn\u2019t the answer." },
      { title: 'The Ankle Cuffs', body: "The softer counterpart to the handcuffs. Padded PU leather with gold-finish hardware, adjustable 7–12\". Balances the set: rigid at the wrists, forgiving at the ankles, by design." },
      { title: 'The Paddle & Whip', body: "A firm stitched-edge paddle paired with a short whip. Two tools, two sounds, two levels of impact. Both balanced for the hand, both built for couples who know the difference between the two." },
      { title: 'The Blindfold, Gag & Clamps', body: "The quieter instruments. Leather blindfold with a satin inner lining, silicone ball gag, adjustable chain nipple clamps. The small tools that turn a scene — no less considered than the large ones." }
    ],
    relatedFamilies: ['standard-10pc', '15pc-set', 'whip-set-8pc']
  },

  'whip-set-8pc': {
    breadcrumbCategory: 'Impact Play',
    titleHead: 'The 8-Piece',
    titleEm: 'Whip Kit',
    mainImage: KBD_IMAGE_FLAT,
    thumbImages: [KBD_IMAGE_FLAT, KBD_IMAGE_DETAIL, KBD_IMAGE_EDITORIAL, KBD_IMAGE_HERO],
    description: "Eight impact tools curated for range. Not a starter kit — the set for someone who already knows the difference between a flogger and a single-tail and wants both within reach. Includes a carrying bag.",
    includedList: [
      '1 × long whip (braided PU leather, 60cm)',
      '1 × flogger (14 falls, short throw)',
      '1 × diamond whip (woven PU leather, focused strike)',
      '1 × firm PU leather paddle',
      '1 × silver toothed roller',
      '1 × braided-handle whip (short, controlled)',
      '1 × rattan cane (natural, 45cm)',
      '1 × handwoven single-tail (red/black)',
      '1 × matte fabric carrying bag'
    ],
    materialsHtml: '<p><strong>Primary materials:</strong> Braided PU leather (whips, flogger), rattan (cane), handwoven leather (single-tail), bamboo-core handles<br>'
      + '<strong>Hardware:</strong> Riveted handles, wrist loops on every piece<br>'
      + '<strong>Long whip:</strong> 60cm, braided PU leather falls<br>'
      + '<strong>Flogger:</strong> 14 falls, 40cm length<br>'
      + '<strong>Rattan cane:</strong> 45cm, natural rattan<br>'
      + '<strong>Single-tail:</strong> Handwoven red and black leather, 90cm<br>'
      + '<strong>Bag:</strong> Water-resistant fabric, padded<br>'
      + '<strong>Weight:</strong> 2.1 lbs (packaged)</p>',
    careList: KBD_CARE_IMPACT,
    piecesTitle: 'The Tools.',
    piecesSub: 'Eight impact instruments, organised by use.',
    pieces: [
      { title: 'The Long Whip', body: "Braided PU leather, 60cm. Mid-range reach, mid-range weight. The default tool if you reach into the bag without thinking about it. Consistent strike, long life, easy to condition." },
      { title: 'The Flogger & Diamond Whip', body: "The flogger — fourteen falls, short throw — is the tool for warming someone up or sustaining a long scene without intensity escalating. The diamond whip is its opposite number: narrower strike, focused impact, closer to a slap than a thud." },
      { title: 'The Rattan Cane', body: "Natural rattan, 45cm. A cane is not a whip — different tempo, different effect, different recovery. Included because a complete impact kit has one. Light, precise, with a sound and sting that\u2019s distinctly itself." },
      { title: 'The Braided Handle & Single-Tail', body: "Two shorter whips for closer work. The braided-handle whip stays controlled in small spaces. The handwoven red-and-black single-tail is for the scenes that call for one — shorter throw than a long whip, more precision." },
      { title: 'The Paddle & Roller', body: "A firm PU leather paddle for broad strokes and a weighted silver roller for warmup, transition, and recovery. The roller isn\u2019t an impact tool — it\u2019s what rounds the set out, so you\u2019re not mid-scene without a way to shift down." }
    ],
    relatedFamilies: ['15pc-set', 'luxury-8pc', 'bondage-jacket-m']
  },

  'bondage-jacket-m': {
    breadcrumbCategory: 'Bondage Jackets',
    titleHead: 'The Bondage Jacket',
    titleEm: '(M)',
    mainImage: KBD_IMAGE_FLAT,
    thumbImages: [KBD_IMAGE_FLAT, KBD_IMAGE_DETAIL, KBD_IMAGE_EDITORIAL, KBD_IMAGE_HERO],
    description: "A structured bondage jacket for established practice. Integrated arm restraint, six D-ring attachment points, and adjustable strapping throughout. A single garment for full-body restraint scenes — a statement piece, not an everyday one.",
    includedList: [
      '1 × bondage jacket (size M)',
      '1 × integrated 95cm body strap',
      'Back lacing system',
      'Adjustable arm sleeve straps',
      'Storage pouch'
    ],
    materialsHtml: '<p><strong>Primary material:</strong> Leathery PU, double-stitched at every stress point<br>'
      + '<strong>Lining:</strong> Polyester against the skin<br>'
      + '<strong>Hardware:</strong> Nickel-free matte alloy D-rings (six points: chest front, chest back, both sides at ribs, both shoulders)<br>'
      + '<strong>Size M:</strong> Chest 32–40", torso length 22", adjustable arm sleeves<br>'
      + '<strong>Strap:</strong> 95cm integrated body strap<br>'
      + '<strong>Weight:</strong> 1.6 lbs (packaged)</p>',
    careList: KBD_CARE_JACKET,
    piecesTitle: 'The Garment.',
    piecesSub: 'Four things to know before you wear one.',
    pieces: [
      { title: 'The Construction', body: "Structured PU leather body harness with integrated arm sleeves. Double-stitched at every stress point — shoulders, ribs, cuff junctions. Polyester lining so it sits against skin without irritation during a long scene." },
      { title: 'The Restraint Points', body: "Six nickel-free D-rings distributed across the body: chest front, chest back, both sides at the ribs, and at each shoulder. Each ring accepts any standard clip, carabiner, or rope. The jacket doesn\u2019t dictate how you use it — it gives you options." },
      { title: 'Fit & Adjustability', body: "Adjustable 95cm body strap, back lacing, arm sleeve straps. The garment sizes to the wearer, not the other way around. Size M fits chest 32–40\". Size up if you\u2019re between sizes — comfort matters more than tightness here." },
      { title: "What It\u2019s For", body: "A jacket is a commitment — to a scene, to the time it takes to put on, to the practice it asks for. If you\u2019re new to restraint, this isn\u2019t the first piece you buy. If you have established practice, it consolidates what you already know into one garment." }
    ],
    relatedFamilies: ['bondage-jacket-l', 'half-jacket-m', 'half-jacket-s']
  },

  'bondage-jacket-l': {
    breadcrumbCategory: 'Bondage Jackets',
    titleHead: 'The Bondage Jacket',
    titleEm: '(L)',
    mainImage: KBD_IMAGE_FLAT,
    thumbImages: [KBD_IMAGE_FLAT, KBD_IMAGE_DETAIL, KBD_IMAGE_EDITORIAL, KBD_IMAGE_HERO],
    description: "The large-size bondage jacket. Same construction as the Medium — integrated arm restraint, six D-ring attachment points, adjustable strapping throughout — sized for a chest range of 38 to 46 inches.",
    includedList: [
      '1 × bondage jacket (size L)',
      '1 × integrated 110cm body strap',
      'Back lacing system',
      'Adjustable arm sleeve straps',
      'Storage pouch'
    ],
    materialsHtml: '<p><strong>Primary material:</strong> Leathery PU, double-stitched at every stress point<br>'
      + '<strong>Lining:</strong> Polyester against the skin<br>'
      + '<strong>Hardware:</strong> Nickel-free matte alloy D-rings (six points)<br>'
      + '<strong>Size L:</strong> Chest 38–46", torso length 24", adjustable arm sleeves<br>'
      + '<strong>Strap:</strong> 110cm integrated body strap<br>'
      + '<strong>Weight:</strong> 1.9 lbs (packaged)</p>',
    careList: KBD_CARE_JACKET,
    piecesTitle: 'The Garment.',
    piecesSub: 'Four things to know before you wear one.',
    pieces: [
      { title: 'The Construction', body: "Structured PU leather body harness with integrated arm sleeves. Double-stitched at every stress point — shoulders, ribs, cuff junctions. Polyester lining so it sits against skin without irritation during a long scene." },
      { title: 'The Restraint Points', body: "Six nickel-free D-rings distributed across the body: chest front, chest back, both sides at the ribs, and at each shoulder. Accepts any standard clip, carabiner, or rope. Designed for flexibility in how a scene is constructed." },
      { title: 'Fit & Adjustability', body: "Adjustable 110cm body strap, back lacing, arm sleeve straps. Size L fits a chest range of 38 to 46 inches. If you\u2019re between sizes, size up — comfort over tightness." },
      { title: "What It\u2019s For", body: "A jacket is a commitment to a scene and to the practice it asks for. For couples with established practice, it consolidates wrist, body, and arm restraint into one garment. Not the first piece you buy. One of the pieces you buy when you already know why." }
    ],
    relatedFamilies: ['bondage-jacket-m', 'half-jacket-m', 'half-jacket-s']
  },

  'half-jacket-s': {
    breadcrumbCategory: 'Bondage Jackets',
    titleHead: 'The Half Jacket',
    titleEm: '(S)',
    mainImage: KBD_IMAGE_FLAT,
    thumbImages: [KBD_IMAGE_FLAT, KBD_IMAGE_DETAIL, KBD_IMAGE_EDITORIAL, KBD_IMAGE_HERO],
    description: "A body harness in jacket form — structured across the torso, without integrated arm restraint. The jacket for when a full bondage jacket is too much commitment. Includes a 95cm body strap.",
    includedList: [
      '1 × half jacket (size S)',
      '1 × 95cm body strap',
      'Adjustable side straps',
      'Storage pouch'
    ],
    materialsHtml: '<p><strong>Primary material:</strong> Leathery PU, polyester lining<br>'
      + '<strong>Hardware:</strong> Nickel-free matte alloy D-rings (four points: chest front, chest back, sides)<br>'
      + '<strong>Size S:</strong> Chest 28–34", torso length 18"<br>'
      + '<strong>Strap:</strong> 95cm body strap, removable<br>'
      + '<strong>Weight:</strong> 0.8 lbs (packaged)</p>',
    careList: KBD_CARE_JACKET,
    piecesTitle: 'The Garment.',
    piecesSub: 'Four things to know before you wear one.',
    pieces: [
      { title: 'The Construction', body: "Torso-only harness in structured PU leather. No arm sleeves — leaves the arms free. Double-stitched at the shoulders and strap junctions, lined with polyester. Simpler to put on than a full jacket, less commitment to wear." },
      { title: 'The Restraint Points', body: "Four D-rings at chest front, chest back, and both sides. Accepts clips, carabiners, or rope. Enough to build a scene; not so many it overcomplicates the garment." },
      { title: 'Fit & Adjustability', body: "Adjustable side straps and a removable 95cm body strap. Size S fits chest 28–34\". If you want restraint but not full arm immobilisation, this is the middle ground." },
      { title: "What It\u2019s For", body: "A half jacket is a step between a body harness and a full bondage jacket. Good for couples expanding practice, or for the scenes where full restraint isn\u2019t the point — only the frame of it." }
    ],
    relatedFamilies: ['half-jacket-m', 'bondage-jacket-m', 'luxury-8pc']
  },

  'half-jacket-m': {
    breadcrumbCategory: 'Bondage Jackets',
    titleHead: 'The Half Jacket',
    titleEm: '(M)',
    mainImage: KBD_IMAGE_FLAT,
    thumbImages: [KBD_IMAGE_FLAT, KBD_IMAGE_DETAIL, KBD_IMAGE_EDITORIAL, KBD_IMAGE_HERO],
    description: "The medium-size half jacket. Same construction as the Small — torso harness, four D-ring points, adjustable side straps — sized for a chest range of 34 to 40 inches. Includes a 95cm body strap.",
    includedList: [
      '1 × half jacket (size M)',
      '1 × 95cm body strap',
      'Adjustable side straps',
      'Storage pouch'
    ],
    materialsHtml: '<p><strong>Primary material:</strong> Leathery PU, polyester lining<br>'
      + '<strong>Hardware:</strong> Nickel-free matte alloy D-rings (four points)<br>'
      + '<strong>Size M:</strong> Chest 34–40", torso length 20"<br>'
      + '<strong>Strap:</strong> 95cm body strap, removable<br>'
      + '<strong>Weight:</strong> 0.9 lbs (packaged)</p>',
    careList: KBD_CARE_JACKET,
    piecesTitle: 'The Garment.',
    piecesSub: 'Four things to know before you wear one.',
    pieces: [
      { title: 'The Construction', body: "Torso-only harness in structured PU leather, sized for chest 34–40\". Double-stitched at the shoulders and strap junctions, polyester-lined. Simpler to put on than a full bondage jacket; less commitment to wear." },
      { title: 'The Restraint Points', body: "Four D-rings at chest front, chest back, and both sides. Enough attachment variety to build a scene; few enough to keep the garment clean." },
      { title: 'Fit & Adjustability', body: "Adjustable side straps and a removable 95cm body strap. The garment sizes to the wearer across a real chest range. Size up if between." },
      { title: "What It\u2019s For", body: "The middle ground between a body harness and a full bondage jacket. For couples who want structure, attachment points, and the shape of a scene without full arm immobilisation." }
    ],
    relatedFamilies: ['half-jacket-s', 'bondage-jacket-m', 'luxury-8pc']
  },

};

const KBD_PRODUCTS = [
  // The Bed Restraint (5 Piece)
  { handle: 'bed-restraint-5-piece-black', family: 'bed-restraint-5pc', name: 'The Bed Restraint (5 Piece)', color: 'Black', colorSwatch: 'black', price: 36, inventory: 1759, inStock: true, bestseller: true, category: 'restraint-sets', experienceLevel: 'beginner', fnsku: 'X003U2HGTT', asin: 'B0C635T2HB' },
  { handle: 'bed-restraint-5-piece-red', family: 'bed-restraint-5pc', name: 'The Bed Restraint (5 Piece)', color: 'Red', colorSwatch: 'red', price: 36, inventory: 494, inStock: true, category: 'restraint-sets', experienceLevel: 'beginner', fnsku: 'X003U2PNE9', asin: 'B0C633NFQ6' },
  { handle: 'bed-restraint-5-piece-pink', family: 'bed-restraint-5pc', name: 'The Bed Restraint (5 Piece)', color: 'Pink', colorSwatch: 'pink', price: 36, inventory: 470, inStock: true, category: 'restraint-sets', experienceLevel: 'beginner', fnsku: 'X003U2PNFX', asin: 'B0C636BWDJ' },
  // The Complete (15 Piece)
  { handle: '15-piece-set-black', family: '15pc-set', name: 'The Complete (15 Piece)', color: 'Black', colorSwatch: 'black', price: 78, inventory: 1284, inStock: true, bestseller: true, category: 'complete-kits', experienceLevel: 'intermediate', fnsku: 'X003U2PQPZ', asin: 'B0C63B8ZR2' },
  { handle: '15-piece-set-red', family: '15pc-set', name: 'The Complete (15 Piece)', color: 'Red', colorSwatch: 'red', price: 78, inventory: 367, inStock: true, category: 'complete-kits', experienceLevel: 'intermediate', fnsku: 'X003U2PNG7', asin: 'B0C6339Y46' },
  { handle: '15-piece-set-pink', family: '15pc-set', name: 'The Complete (15 Piece)', color: 'Pink', colorSwatch: 'pink', price: 78, inventory: 291, inStock: true, category: 'complete-kits', experienceLevel: 'intermediate', fnsku: 'X003U2PP5V', asin: 'B0C63CS3BF' },
  { handle: '15-piece-set-purple', family: '15pc-set', name: 'The Complete (15 Piece)', color: 'Purple', colorSwatch: 'purple', price: 78, inventory: 299, inStock: true, category: 'complete-kits', experienceLevel: 'intermediate', fnsku: 'X003U2HGUD', asin: 'B0C62TJHT5' },
  // The Standard (10 Piece) (hero product)
  { handle: '10-piece-standard-set-black', family: 'standard-10pc', name: 'The Standard (10 Piece)', color: 'Black', colorSwatch: 'black', price: 64, inventory: 1885, inStock: true, bestseller: true, hero: true, category: 'restraint-sets', experienceLevel: 'beginner', fnsku: 'X003U2PNET', asin: 'B0C63BXBBX' },
  { handle: '10-piece-standard-set-red', family: 'standard-10pc', name: 'The Standard (10 Piece)', color: 'Red', colorSwatch: 'red', price: 64, inventory: 444, inStock: true, category: 'restraint-sets', experienceLevel: 'beginner', fnsku: 'X003U2PP79', asin: 'B0C637XD9V' },
  { handle: '10-piece-standard-set-pink', family: 'standard-10pc', name: 'The Standard (10 Piece)', color: 'Pink', colorSwatch: 'pink', price: 64, inventory: 462, inStock: true, category: 'restraint-sets', experienceLevel: 'beginner', fnsku: 'X003U2PQQT', asin: 'B0C632YKX7' },
  { handle: '10-piece-standard-set-purple', family: 'standard-10pc', name: 'The Standard (10 Piece)', color: 'Purple', colorSwatch: 'purple', price: 64, inventory: 442, inStock: true, category: 'restraint-sets', experienceLevel: 'beginner', fnsku: 'X003U2HGTJ', asin: 'B0C63FN7BQ' },
  // The Luxury (8 Piece)
  { handle: 'luxury-8-piece-set-black', family: 'luxury-8pc', name: 'The Luxury (8 Piece)', color: 'Black', colorSwatch: 'black', price: 92, inventory: 52, inStock: true, lowStock: true, category: 'restraint-sets', experienceLevel: 'intermediate', fnsku: 'X003U2PQRN', asin: 'B0C6364TSV' },
  // 8-Piece Whip Kit
  { handle: '8-piece-whip-set-black', family: 'whip-set-8pc', name: 'The Impact (8 Piece)', color: 'Black', colorSwatch: 'black', price: 74, inventory: 72, inStock: true, lowStock: true, category: 'impact-play', experienceLevel: 'advanced', fnsku: 'X003U2PNGR', asin: 'B0C63CH56Y' },
  // Bondage Jacket (M)
  { handle: 'bondage-jacket-m-black', family: 'bondage-jacket-m', name: 'Bondage Jacket (M)', color: 'Black', colorSwatch: 'black', price: 89, inventory: 374, inStock: true, category: 'jackets', experienceLevel: 'advanced', size: 'M', fnsku: 'X003U77JSH', asin: 'B0BZZ36FPW' },
  { handle: 'bondage-jacket-m-white', family: 'bondage-jacket-m', name: 'Bondage Jacket (M)', color: 'White', colorSwatch: 'white', price: 89, inventory: 170, inStock: true, category: 'jackets', experienceLevel: 'advanced', size: 'M', fnsku: 'X003U2HGU3', asin: 'B0C66YWJ9M' },
  { handle: 'bondage-jacket-m-rose-red', family: 'bondage-jacket-m', name: 'Bondage Jacket (M)', color: 'Rose Red', colorSwatch: 'rose-red', price: 89, inventory: 180, inStock: true, category: 'jackets', experienceLevel: 'advanced', size: 'M', fnsku: 'X003U2PNF3', asin: 'B0C67DVGN3' },
  { handle: 'bondage-jacket-m-red', family: 'bondage-jacket-m', name: 'Bondage Jacket (M)', color: 'Red', colorSwatch: 'red', price: 89, inventory: 184, inStock: true, category: 'jackets', experienceLevel: 'advanced', size: 'M', fnsku: 'X003U2PP6P', asin: 'B0C66WY521' },
  // Bondage Jacket (L)
  { handle: 'bondage-jacket-l-black', family: 'bondage-jacket-l', name: 'Bondage Jacket (L)', color: 'Black', colorSwatch: 'black', price: 99, inventory: 165, inStock: true, category: 'jackets', experienceLevel: 'advanced', size: 'L', fnsku: 'X003U2PQRD', asin: 'B0C66GZ664' },
  // Half Jacket (S)
  { handle: 'half-jacket-s-black', family: 'half-jacket-s', name: 'Half Jacket (S)', color: 'Black', colorSwatch: 'black', price: 54, inventory: 295, inStock: true, category: 'jackets', experienceLevel: 'intermediate', size: 'S', fnsku: 'X003U2PP6Z', asin: 'B0C66VY2B6' },
  { handle: 'half-jacket-s-red-black', family: 'half-jacket-s', name: 'Half Jacket (S)', color: 'Red/Black', colorSwatch: 'red-black', price: 54, inventory: 200, inStock: true, category: 'jackets', experienceLevel: 'intermediate', size: 'S', fnsku: 'X003U2HGT9', asin: 'B0C67F94LY' },
  { handle: 'half-jacket-s-pink', family: 'half-jacket-s', name: 'Half Jacket (S)', color: 'Pink', colorSwatch: 'pink', price: 54, inventory: 132, inStock: true, category: 'jackets', experienceLevel: 'intermediate', size: 'S', fnsku: 'X003U2PQQJ', asin: 'B0C67B5J12' },
  // Half Jacket (M)
  { handle: 'half-jacket-m-black', family: 'half-jacket-m', name: 'Half Jacket (M)', color: 'Black', colorSwatch: 'black', price: 64, inventory: 269, inStock: true, category: 'jackets', experienceLevel: 'intermediate', size: 'M', fnsku: 'X003U2PNEJ', asin: 'B0C674N9PB' }
];

const KBD_PRODUCTS_BY_FAMILY = {};
KBD_PRODUCTS.forEach(p => {
  if (!KBD_PRODUCTS_BY_FAMILY[p.family]) KBD_PRODUCTS_BY_FAMILY[p.family] = [];
  KBD_PRODUCTS_BY_FAMILY[p.family].push(p);
});

function kbdFormatPrice(n) {
  return '$' + Number(n).toFixed(2);
}

// ═══════════════════════════════════════════════════════════════════
// COLLECTIONS — data-driven shop/collection pages
// One [data-page="shop"] skeleton is populated by renderCollection()
// from KBD_COLLECTIONS. Route pattern: navigate('collection-[handle]').
// Plain navigate('shop') renders the 'all' collection.
// Mirrors Shopify's collection.liquid pattern.
// ═══════════════════════════════════════════════════════════════════

function kbdProductTags(p) {
  const tags = [
    'cat-' + p.category,
    'level-' + p.experienceLevel,
    'color-' + p.colorSwatch
  ];
  if (p.bestseller) tags.push('bestseller');
  if (p.lowStock) tags.push('low-stock');
  if (!p.inStock) tags.push('out-of-stock');
  if (p.size) tags.push('size-' + String(p.size).toLowerCase());
  return tags.join(' ');
}

// ═══════════════════════════════════════════════════════════════════
// CART — live Shopify integration via /cart/*.js API. No page reloads.
// Single source of truth: KBD_CART_STATE mirrors the latest /cart.js
// response. All mutations re-fetch and re-render. Drawer, header badge,
// checkout page, and thank-you all read from this state.
// ═══════════════════════════════════════════════════════════════════

let KBD_CART_STATE = null; // latest /cart.js response
let KBD_LAST_ORDER = null; // retained for the thank-you mockup page

function kbdCartMoney(cents) {
  return '$' + (Math.round(Number(cents) || 0) / 100).toFixed(2);
}

async function kbdCartFetch() {
  try {
    const r = await fetch('/cart.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    });
    if (r.ok) KBD_CART_STATE = await r.json();
  } catch (e) {
    console.warn('KBD cart fetch failed', e);
  }
  if (!KBD_CART_STATE) KBD_CART_STATE = { items: [], item_count: 0, total_price: 0 };
  kbdCartRender();
  return KBD_CART_STATE;
}

async function kbdCartAdd(variantId, qty) {
  qty = Math.max(1, parseInt(qty, 10) || 1);
  if (!variantId) return false;
  try {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: String(variantId), quantity: qty })
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      console.warn('KBD cart add failed', err.description || err);
      return false;
    }
    await kbdCartFetch();
    const drawer = document.getElementById('cartDrawer');
    if (drawer && !drawer.classList.contains('is-open')) toggleCart();
    return true;
  } catch (e) {
    console.warn('KBD cart add error', e);
    return false;
  }
}

// Optimistic local edit so the drawer repaints on the same frame as the
// click. /cart/change.js is a round trip to Shopify; awaiting it before
// touching the DOM left the + and - buttons looking dead for the whole
// request, which is what "takes several seconds to adjust" was.
//
// Every request carries an ABSOLUTE quantity, not a delta, so rapid clicks
// converge; kbdCartSeq additionally discards a response that arrives after a
// newer one, so an out-of-order reply cannot resurrect a stale total.
function kbdCartApplyLocalQty(lineKey, qty) {
  if (!KBD_CART_STATE || !Array.isArray(KBD_CART_STATE.items)) return;
  const items = [];
  for (let i = 0; i < KBD_CART_STATE.items.length; i++) {
    const item = KBD_CART_STATE.items[i];
    if (String(item.key) !== String(lineKey)) { items.push(item); continue; }
    if (qty <= 0) continue;
    const unit = (item.final_price != null) ? item.final_price : item.price;
    items.push(Object.assign({}, item, {
      quantity: qty,
      final_line_price: unit * qty,
      line_price: unit * qty,
      original_line_price: (item.original_price != null ? item.original_price : unit) * qty
    }));
  }
  let count = 0;
  let total = 0;
  for (let i = 0; i < items.length; i++) {
    count += items[i].quantity;
    total += (items[i].final_line_price != null) ? items[i].final_line_price : 0;
  }
  KBD_CART_STATE = Object.assign({}, KBD_CART_STATE, {
    items: items,
    item_count: count,
    total_price: total,
    items_subtotal_price: total
  });
}

let kbdCartSeq = 0;

async function kbdCartSetQty(lineKey, qty) {
  qty = Math.max(0, parseInt(qty, 10) || 0);
  if (!lineKey) return false;
  const seq = ++kbdCartSeq;
  const previous = KBD_CART_STATE ? JSON.parse(JSON.stringify(KBD_CART_STATE)) : null;
  kbdCartApplyLocalQty(lineKey, qty);
  kbdCartRender();
  try {
    const r = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: String(lineKey), quantity: qty })
    });
    if (seq !== kbdCartSeq) return r.ok;
    if (r.ok) {
      KBD_CART_STATE = await r.json();
      kbdCartRender();
      return true;
    }
    if (previous) { KBD_CART_STATE = previous; kbdCartRender(); }
    await kbdCartFetch();
    return false;
  } catch (e) {
    console.warn('KBD cart change error', e);
    if (seq === kbdCartSeq) {
      if (previous) { KBD_CART_STATE = previous; kbdCartRender(); }
      await kbdCartFetch();
    }
    return false;
  }
}

function kbdCartRemove(lineKey) { return kbdCartSetQty(lineKey, 0); }

function kbdCartCount() {
  return (KBD_CART_STATE && KBD_CART_STATE.item_count) || 0;
}

function kbdRenderCartDrawer() {
  const itemsEl = document.getElementById('cart-items');
  const footerEl = document.getElementById('cart-footer');
  const subtotalEl = document.getElementById('cart-subtotal');
  const titleEl = document.getElementById('cart-title');
  if (!itemsEl || !footerEl) return;
  const cart = KBD_CART_STATE || { items: [], item_count: 0, total_price: 0 };
  const count = cart.item_count || 0;
  if (titleEl) titleEl.textContent = count > 0 ? ('Your cart (' + count + ')') : 'Your cart';

  if (!cart.items || cart.items.length === 0) {
    itemsEl.innerHTML =
      '<div class="cart-drawer__empty">'
      + '<h4>Your cart is empty.</h4>'
      + '<p>Nothing here yet. Browse the shop to get started.</p>'
      + '<div style="display: inline-flex; gap: 12px; flex-wrap: wrap; justify-content: center;">'
        + '<a href="/collections/all" onclick="toggleCart();" class="btn btn--primary">Shop all</a>'
      + '</div>'
      + '</div>';
    footerEl.style.display = 'none';
    return;
  }

  footerEl.style.display = '';
  itemsEl.innerHTML = '';
  cart.items.forEach(item => {
    const img = item.image || (item.featured_image && item.featured_image.url) || '';
    const lineCents = item.final_line_price != null ? item.final_line_price : item.line_price;
    const keyAttr = kbdEscapeHtml(item.key || '');
    const name = kbdEscapeHtml(item.product_title || item.title || '');
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? kbdEscapeHtml(item.variant_title) : '';
    const row = document.createElement('div');
    row.className = 'cart-item';
    row.setAttribute('data-cart-key', item.key);
    if (item.variant_id) row.setAttribute('data-variant-id', item.variant_id);
    row.innerHTML =
      '<div class="cart-item__thumb"><img src="' + kbdEscapeHtml(img) + '" alt=""></div>'
      + '<div>'
        + '<div class="cart-item__name">' + name + '</div>'
        + (variant ? '<div class="cart-item__variant">' + variant + '</div>' : '')
        + '<div class="cart-item__qty">'
          + '<button type="button" onclick="kbdCartSetQty(\'' + keyAttr + '\', ' + (item.quantity - 1) + ');" aria-label="Decrease quantity">\u2212</button>'
          + '<span class="cart-item__qty-val">' + item.quantity + '</span>'
          + '<button type="button" onclick="kbdCartSetQty(\'' + keyAttr + '\', ' + (item.quantity + 1) + ');" aria-label="Increase quantity">+</button>'
        + '</div>'
      + '</div>'
      + '<div class="cart-item__side">'
        + '<button type="button" class="cart-item__remove" onclick="kbdCartRemove(\'' + keyAttr + '\');" aria-label="Remove ' + name + '">×</button>'
        + '<div class="cart-item__price" data-price="' + lineCents + '">' + kbdCartMoney(lineCents) + '</div>'
      + '</div>';
    itemsEl.appendChild(row);
  });

  if (subtotalEl) {
    subtotalEl.textContent = kbdCartMoney(cart.total_price);
    subtotalEl.setAttribute('data-price', cart.total_price);
  }

  // Discount-threshold progress. Uses cart.items_subtotal_price (the
  // PRE-discount subtotal) so the bar doesn't contradict itself once the
  // founder's automatic discount applies and total_price returns post-discount.
  // Threshold (cents) + label come from theme settings via data attributes on
  // #cart-shipping-progress. DISPLAY ONLY: this reports progress toward a
  // discount; the real discount is a Shopify automatic discount the founder
  // creates.
  const shipProgress = document.getElementById('cart-shipping-progress');
  let discountThreshold = 15000;
  let discountLabel = '10% off';
  if (shipProgress) {
    const t = parseInt(shipProgress.getAttribute('data-threshold-cents'), 10);
    if (!isNaN(t) && t > 0) discountThreshold = t;
    const l = shipProgress.getAttribute('data-discount-label');
    if (l) discountLabel = l;
  }
  const shipFill = document.getElementById('cart-shipping-fill');
  const shipMsg  = document.getElementById('cart-shipping-msg');
  const shipSub  = cart.items_subtotal_price || 0;
  if (shipFill) {
    const pct = Math.min(shipSub / discountThreshold, 1) * 100;
    shipFill.style.width = pct + '%';
  }
  if (shipMsg) {
    if (shipSub >= discountThreshold) {
      shipMsg.textContent = "You've unlocked " + discountLabel + ' — applied at checkout.';
    } else {
      shipMsg.textContent = "You're " + kbdCartMoney(discountThreshold - shipSub) + ' away from ' + discountLabel;
    }
  }
}

function kbdRenderCheckoutSummary() {
  // On real Shopify, checkout is hosted at /checkout — this in-DOM summary
  // only exists on the mockup's internal checkout page, which has no live
  // counterpart. Guard against that node being absent so this is a no-op
  // on every real template.
  const itemsEl = document.getElementById('checkout-items');
  if (!itemsEl) return;
  const cart = KBD_CART_STATE || { items: [], item_count: 0, total_price: 0 };
  const subEl = document.getElementById('checkout-subtotal');
  const totalEl = document.getElementById('checkout-total');
  const btn = document.getElementById('checkout-submit-btn');
  itemsEl.innerHTML = '';
  if (!cart.items || cart.items.length === 0) {
    itemsEl.innerHTML = '<div style="padding: 24px 0; font-size: 13px; color: var(--kbd-smoke); text-align: center;">No items in your cart. <a href="/collections/all" style="text-decoration: underline;">Shop all</a>.</div>';
  } else {
    cart.items.forEach(item => {
      const img = item.image || (item.featured_image && item.featured_image.url) || '';
      const el = document.createElement('div');
      el.className = 'order-summary__item';
      if (item.variant_id) el.setAttribute('data-variant-id', item.variant_id);
      const lineCents = item.final_line_price != null ? item.final_line_price : item.line_price;
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? kbdEscapeHtml(item.variant_title) : '';
      el.innerHTML =
        '<div class="order-summary__thumb">'
          + '<img src="' + kbdEscapeHtml(img) + '" alt="">'
          + '<div class="order-summary__qty">' + item.quantity + '</div>'
        + '</div>'
        + '<div>'
          + '<div class="order-summary__name">' + kbdEscapeHtml(item.product_title || item.title || '') + '</div>'
          + (variant ? '<div class="order-summary__variant">' + variant + '</div>' : '')
        + '</div>'
        + '<div class="order-summary__price" data-price="' + lineCents + '">' + kbdCartMoney(lineCents) + '</div>';
      itemsEl.appendChild(el);
    });
  }
  const totalCents = cart.total_price || 0;
  if (subEl) { subEl.textContent = kbdCartMoney(totalCents); subEl.setAttribute('data-price', totalCents); }
  if (totalEl) { totalEl.textContent = kbdCartMoney(totalCents); totalEl.setAttribute('data-price', totalCents); }
  if (btn) {
    btn.textContent = 'Complete order — ' + kbdCartMoney(totalCents);
    btn.disabled = !cart.items || cart.items.length === 0;
    btn.style.opacity = btn.disabled ? '0.5' : '1';
  }
}

function kbdRenderThankYou() {
  const itemsEl = document.getElementById('thankyou-items');
  const subEl = document.getElementById('thankyou-subtotal');
  const totalEl = document.getElementById('thankyou-total');
  if (!itemsEl) return;
  const order = KBD_LAST_ORDER;
  if (!order || !order.lines || order.lines.length === 0) {
    // Fallback for direct navigation without a submitted order
    itemsEl.innerHTML = '<li><span>No recent order to display.</span></li>';
    if (subEl) subEl.textContent = '$0.00';
    if (totalEl) totalEl.textContent = '$0.00';
    return;
  }
  itemsEl.innerHTML = '';
  order.lines.forEach(line => {
    const li = document.createElement('li');
    li.innerHTML = '<strong>' + line.name + '</strong><br><span>Color: ' + line.color + ' · Qty ' + line.qty + '</span>';
    itemsEl.appendChild(li);
  });
  const subCents = Math.round(order.subtotal * 100);
  if (subEl) { subEl.textContent = kbdFormatPrice(order.subtotal); subEl.setAttribute('data-price', subCents); }
  if (totalEl) { totalEl.textContent = kbdFormatPrice(order.subtotal); totalEl.setAttribute('data-price', subCents); }
}

function kbdRenderCartCount() {
  const el = document.getElementById('cart-count');
  if (!el) return;
  const n = kbdCartCount();
  el.textContent = n;
  if (n === 0) el.classList.add('is-empty');
  else el.classList.remove('is-empty');
}

function kbdCartRender() {
  kbdRenderCartCount();
  kbdRenderCartDrawer();
  kbdRenderCheckoutSummary();
}

// ═══════════════════════════════════════════════════════════════════
// JOURNAL CATALOG — used by search. Mirrors the cards in the journal index.
// ═══════════════════════════════════════════════════════════════════

const KBD_JOURNAL = [
  { handle: 'article-beginners-guide', eyebrow: "Beginner\u2019s Guide", title: "A first-timer\u2019s guide to bondage and restraint", excerpt: "What you actually need, what you don\u2019t, and the mistakes everyone makes early. Read this before anything else.", meta: "8 min read · April 12, 2026" },
  { handle: 'article-aftercare', eyebrow: "Aftercare", title: "Aftercare, explained: the part no one teaches", excerpt: "The twenty minutes after a scene matter as much as the scene itself. Here\u2019s how to do them well.", meta: "6 min read · April 8, 2026" },
  { handle: 'article-care', eyebrow: "Gear Care", title: "How to clean and store your gear so it lasts", excerpt: "Good gear is an investment. These are the five habits that keep it in shape for years.", meta: "5 min read · April 3, 2026" },
  { handle: 'article-safewords', eyebrow: "Safety", title: "Safe words: picking one and why it matters", excerpt: "The stoplight system, why your favorite word is a bad safe word, and what to do if yours stops working.", meta: "4 min read · March 28, 2026" },
  { handle: 'article-beginners-kit', eyebrow: "Beginner\u2019s Guide", title: "The beginner\u2019s kit: what you actually need", excerpt: "The pieces every starter kit should have, the ones you can safely skip, and the dry run nobody tells you to do.", meta: "7 min read · March 22, 2026" },
  { handle: 'article-partner-conversation', eyebrow: "Communication", title: "Talking to your partner about trying bondage", excerpt: "How to start the conversation without making it weird. Scripts, timing, and what not to say first.", meta: "9 min read · March 18, 2026" },
  { handle: 'article-restraint-play', eyebrow: "Beginner\u2019s Guide", title: "Understanding restraint play: soft, medium, firm", excerpt: "Three intensities, three different conversations. Here\u2019s how to know which you want — and tell your partner.", meta: "6 min read · March 12, 2026" },
  { handle: 'article-cuffs-rope-tape', eyebrow: "Gear Guide", title: "Choosing between cuffs, rope, and tape", excerpt: "Three ways to restrain. Cuffs are fastest. Rope is most expressive. Tape is a specialty. Here\u2019s how to pick your first.", meta: "7 min read · March 6, 2026" },
  { handle: 'article-body-safe-materials', eyebrow: "Safety", title: "Body-safe materials: what to look for (and avoid)", excerpt: "The ingredients list most brands won\u2019t publish, and why it should be your first filter before buying anything.", meta: "8 min read · March 1, 2026" },
  { handle: 'article-impact-play', eyebrow: "Gear Guide", title: "Impact play basics: paddles, whips, and what they do", excerpt: "Each tool produces a different sound, sensation, and effect. Here\u2019s what they do, when to use them, and the one to start with.", meta: "8 min read · February 22, 2026" },
  { handle: 'article-bondage-jackets', eyebrow: "Gear Guide", title: "Bondage jackets 101: what they are, how to use them", excerpt: "Not a starter piece. A full-body garment for established practice. Here\u2019s what a jacket does that cuffs can\u2019t, and when it fits.", meta: "8 min read · February 15, 2026" },
  { handle: 'article-building-a-scene', eyebrow: "Beginner\u2019s Guide", title: "Building a scene: structure for first-time dom/subs", excerpt: "Most first scenes fall apart because they had no shape. Here\u2019s the three-part arc — warmup, scene, return — that keeps a practice together.", meta: "8 min read · February 8, 2026" }
];

// ═══════════════════════════════════════════════════════════════════
// SEARCH — real-time product + article match on /search page
// Matches case-insensitively against product name/color/category/tags
// and article title/excerpt/eyebrow. Renders into two sections.
// ═══════════════════════════════════════════════════════════════════

function kbdEscapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[c]);
}

function kbdRunSearch() {
  const input = document.getElementById('search-input');
  if (!input) return;
  const rawQ = input.value.trim();
  const q = rawQ.toLowerCase();
  const emptyEl = document.getElementById('search-empty');
  const productsSection = document.getElementById('search-products-section');
  const articlesSection = document.getElementById('search-articles-section');
  const productsGrid = document.getElementById('search-products-grid');
  const articlesGrid = document.getElementById('search-articles-grid');
  const productsCount = document.getElementById('search-products-count');
  const articlesCount = document.getElementById('search-articles-count');
  if (!emptyEl || !productsSection) return;

  if (q.length === 0) {
    // Landing state: suggestion chips
    productsSection.style.display = 'none';
    articlesSection.style.display = 'none';
    emptyEl.style.display = '';
    emptyEl.innerHTML =
      '<p>Start typing to search products and articles. Some suggestions:</p>'
      + '<div class="search-landing__suggestions">'
      + '<button type="button" onclick="kbdSearchFor(\'10-piece\')">10-piece</button>'
      + '<button type="button" onclick="kbdSearchFor(\'jacket\')">jacket</button>'
      + '<button type="button" onclick="kbdSearchFor(\'rope\')">rope</button>'
      + '<button type="button" onclick="kbdSearchFor(\'aftercare\')">aftercare</button>'
      + '<button type="button" onclick="kbdSearchFor(\'black\')">black</button>'
      + '<button type="button" onclick="kbdSearchFor(\'beginner\')">beginner</button>'
      + '</div>';
    return;
  }

  // Match products
  const productMatches = KBD_PRODUCTS.filter(p => {
    const haystack = [
      p.name || '',
      p.color || '',
      p.category || '',
      p.family || '',
      KBD_CATEGORY_LABELS[p.category] || '',
      KBD_LEVEL_LABELS[p.experienceLevel] || '',
      kbdProductTags(p).replace(/-/g, ' ')
    ].join(' ').toLowerCase();
    return haystack.indexOf(q) !== -1;
  });

  // Match journal articles
  const articleMatches = KBD_JOURNAL.filter(a => {
    const haystack = (a.title + ' ' + a.excerpt + ' ' + a.eyebrow).toLowerCase();
    return haystack.indexOf(q) !== -1;
  });

  // Render products
  if (productMatches.length > 0) {
    productsSection.style.display = '';
    productsCount.textContent = '(' + productMatches.length + ')';
    productsGrid.innerHTML = '';
    productMatches.forEach(p => {
      const famData = KBD_PRODUCT_FAMILIES[p.family];
      const img = (famData && famData.mainImage) || KBD_IMAGE_FLAT;
      const card = document.createElement('a');
      card.href = '#';
      card.className = 'product-card';
      card.setAttribute('data-product-handle', p.handle);
      if (p.fnsku) card.setAttribute('data-product-id', p.fnsku);
      if (p.fnsku) card.setAttribute('data-variant-id', p.fnsku);
      card.onclick = function(e) { e.preventDefault(); navigate('pdp-' + p.handle); return false; };
      let chipHtml = '';
      if (p.bestseller) chipHtml = '<div class="product-card__chip">Bestseller</div>';
      else if (!p.inStock) chipHtml = '<div class="product-card__chip" style="background: var(--kbd-site-ink); color: var(--kbd-bone);">Out of stock</div>';
      const quickAddHtml = p.inStock
        ? '<button class="product-card__quick-add" onclick="event.preventDefault(); event.stopPropagation(); kbdCartAdd(\'' + p.handle + '\');">+</button>'
        : '';
      card.innerHTML =
        '<div class="product-card__image">'
          + chipHtml
          + '<img class="product-card__svg" src="' + img + '" alt="">'
          + quickAddHtml
        + '</div>'
        + '<h4>' + p.name + '</h4>'
        + '<div class="product-card__color">' + p.color + '</div>'
        + '<div class="product-card__price" data-price="' + Math.round(p.price * 100) + '">' + kbdFormatPrice(p.price) + '</div>';
      productsGrid.appendChild(card);
    });
  } else {
    productsSection.style.display = 'none';
  }

  // Render articles
  if (articleMatches.length > 0) {
    articlesSection.style.display = '';
    articlesCount.textContent = '(' + articleMatches.length + ')';
    articlesGrid.innerHTML = '';
    articleMatches.forEach(a => {
      const card = document.createElement('a');
      card.href = '#';
      card.className = 'journal-card';
      card.onclick = function(e) { e.preventDefault(); navigate(a.handle); return false; };
      card.innerHTML =
        '<div class="journal-card__image"><img src="' + KBD_IMAGE_EDITORIAL + '" alt=""></div>'
        + '<div class="eyebrow journal-card__eyebrow">' + a.eyebrow + '</div>'
        + '<h4>' + a.title + '</h4>'
        + '<p class="journal-card__excerpt">' + a.excerpt + '</p>'
        + '<div class="journal-card__meta">' + a.meta + '</div>';
      articlesGrid.appendChild(card);
    });
  } else {
    articlesSection.style.display = 'none';
  }

  // Empty state when nothing matches
  if (productMatches.length === 0 && articleMatches.length === 0) {
    emptyEl.style.display = '';
    emptyEl.innerHTML =
      '<h3>No matches for "' + kbdEscapeHtml(rawQ) + '"</h3>'
      + '<p>Try a different term, or explore another way in.</p>'
      + '<div class="search-noresult__actions">'
        + '<a href="#" onclick="navigate(\'shop\'); return false;" class="btn btn--primary">Shop all products</a>'
      + '</div>';
  } else {
    emptyEl.style.display = 'none';
  }
}

function kbdSearchFor(term) {
  const input = document.getElementById('search-input');
  if (!input) return;
  input.value = term;
  input.focus();
  kbdRunSearch();
}

// ═══════════════════════════════════════════════════════════════════
// COLLECTION FILTERS — sidebar narrow-down on the visible grid
// Reads checkbox + color-swatch state, hides non-matching cards,
// updates the "N products" count, renders filter chips above the grid.
// Filters are additive: each group is OR internally, groups are AND.
// ═══════════════════════════════════════════════════════════════════

const KBD_CHIP_LABELS = {
  'cat-restraint-set': 'Restraint Kits',
  'cat-jacket': 'Bondage Jackets',
  'cat-impact': 'Impact Play',
  'cat-kit': 'Complete Kits',
  'level-beginner': 'New to play',
  'level-intermediate': 'Intermediate',
  'level-advanced': 'Advanced',
  'color-black': 'Black',
  'color-red': 'Red',
  'color-pink': 'Pink',
  'color-purple': 'Purple',
  'color-white': 'White',
  'color-rose-red': 'Rose Red',
  'color-red-black': 'Red / Black'
};

function kbdGetActiveFilters() {
  const sidebar = document.getElementById('filter-sidebar');
  if (!sidebar) return null;
  const groups = {};
  sidebar.querySelectorAll('input[type="checkbox"][data-filter-tag]:checked').forEach(cb => {
    const groupWrap = cb.closest('[data-filter-group]');
    const group = groupWrap ? groupWrap.getAttribute('data-filter-group') : 'other';
    if (!groups[group]) groups[group] = new Set();
    groups[group].add(cb.getAttribute('data-filter-tag'));
  });
  sidebar.querySelectorAll('.color-swatch.is-selected[data-filter-tag]').forEach(sw => {
    if (!groups.color) groups.color = new Set();
    groups.color.add(sw.getAttribute('data-filter-tag'));
  });
  const minEl = sidebar.querySelector('[data-filter-role="price-min"]');
  const maxEl = sidebar.querySelector('[data-filter-role="price-max"]');
  const min = minEl && minEl.value !== '' ? Number(minEl.value) : null;
  const max = maxEl && maxEl.value !== '' ? Number(maxEl.value) : null;
  const instockEl = sidebar.querySelector('[data-filter-role="in-stock-only"]');
  const inStockOnly = !!(instockEl && instockEl.checked);
  return { groups, min, max, inStockOnly };
}

function kbdApplyFilters() {
  if (typeof window.Shopify !== 'undefined') return;
  const grid = document.getElementById('collection-grid');
  if (!grid) return;
  const filters = kbdGetActiveFilters();
  if (!filters) return;

  const searchInputEl = document.getElementById('shop-search-input');
  const searchQ = searchInputEl ? searchInputEl.value.trim().toLowerCase() : '';

  let visible = 0;
  grid.querySelectorAll('.product-card').forEach(card => {
    const handle = card.getAttribute('data-product-handle');
    const product = handle ? KBD_PRODUCTS_BY_HANDLE[handle] : null;
    const tags = (card.getAttribute('data-tags') || '').split(/\s+/).filter(Boolean);

    let match = true;
    // Each group: product must contain at least one tag from the group (OR within group)
    for (const groupName in filters.groups) {
      const tagSet = filters.groups[groupName];
      if (tagSet.size === 0) continue;
      let anyMatch = false;
      for (const t of tagSet) {
        if (tags.indexOf(t) !== -1) { anyMatch = true; break; }
      }
      if (!anyMatch) { match = false; break; }
    }
    if (match && product) {
      if (filters.min !== null && product.price < filters.min) match = false;
      if (filters.max !== null && product.price > filters.max) match = false;
      if (filters.inStockOnly && !product.inStock) match = false;
    }

    // Search: narrow within the filter set. Match name, color, category.
    if (match && searchQ && product) {
      const haystack = [
        product.name || '',
        product.color || '',
        (product.category || '').replace(/-/g, ' ')
      ].join(' ').toLowerCase();
      if (haystack.indexOf(searchQ) === -1) match = false;
    }

    card.style.display = match ? '' : 'none';
    if (match) visible++;
  });

  const countEl = document.getElementById('collection-count');
  if (countEl) countEl.textContent = visible + (visible === 1 ? ' product' : ' products');

  // No-results message — only shown when a search is active and nothing matches.
  // Keeps existing filter-only empty behaviour unchanged.
  const emptyEl = document.getElementById('shop-search-empty');
  if (emptyEl) emptyEl.style.display = (searchQ && visible === 0) ? '' : 'none';

  // Load-more is only meaningful when a batching layer holds back some
  // matching cards. No such layer exists today, so every matching card
  // is already on screen and the button stays hidden. Future pagination
  // can mark held-back cards with .is-paginated-off to re-enable it.
  const loadMoreBtn = document.getElementById('shop-load-more');
  if (loadMoreBtn) {
    const held = grid.querySelectorAll('.product-card.is-paginated-off').length;
    loadMoreBtn.style.display = held > 0 ? '' : 'none';
  }

  kbdUpdateChips(filters);
}

// Filter-sidebar accordion: each group opens/closes independently.
// Multiple groups may be open at once; a group closes only when its own
// header is clicked. Static groups (e.g. Price) have no header button.
function kbdToggleFilterGroup(headerEl) {
  const group = headerEl && headerEl.parentElement;
  if (!group) return;
  group.classList.toggle('is-collapsed');
}

function kbdUpdateChips(filters) {
  const chipsEl = document.getElementById('filter-chips');
  if (!chipsEl) return;
  chipsEl.innerHTML = '';
  let anyActive = false;

  for (const groupName in filters.groups) {
    const tagSet = filters.groups[groupName];
    tagSet.forEach(tag => {
      const label = KBD_CHIP_LABELS[tag] || tag;
      const chip = document.createElement('span');
      chip.className = 'filter-chip';
      chip.innerHTML = label + ' <button type="button" aria-label="Remove ' + label + '">×</button>';
      chip.querySelector('button').addEventListener('click', () => kbdRemoveFilterTag(tag));
      chipsEl.appendChild(chip);
      anyActive = true;
    });
  }
  if (filters.min !== null || filters.max !== null) {
    const minLabel = filters.min !== null ? '$' + filters.min : '$0';
    const maxLabel = filters.max !== null ? '$' + filters.max : 'any';
    const chip = document.createElement('span');
    chip.className = 'filter-chip';
    chip.innerHTML = 'Price: ' + minLabel + '–' + maxLabel + ' <button type="button" aria-label="Remove price filter">×</button>';
    chip.querySelector('button').addEventListener('click', () => {
      const sidebar = document.getElementById('filter-sidebar');
      const mi = sidebar.querySelector('[data-filter-role="price-min"]');
      const ma = sidebar.querySelector('[data-filter-role="price-max"]');
      if (mi) mi.value = '';
      if (ma) ma.value = '';
      if (typeof window.kbdResetPriceSlider === 'function') window.kbdResetPriceSlider();
      kbdApplyFilters();
    });
    chipsEl.appendChild(chip);
    anyActive = true;
  }
  if (filters.inStockOnly) {
    const chip = document.createElement('span');
    chip.className = 'filter-chip';
    chip.innerHTML = 'In stock only <button type="button" aria-label="Remove in stock only">×</button>';
    chip.querySelector('button').addEventListener('click', () => {
      const el = document.querySelector('[data-filter-role="in-stock-only"]');
      if (el) el.checked = false;
      kbdApplyFilters();
    });
    chipsEl.appendChild(chip);
    anyActive = true;
  }
  if (anyActive) {
    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'filter-chips__clear';
    clearBtn.textContent = 'Clear all';
    clearBtn.onclick = kbdClearAllFilters;
    chipsEl.appendChild(clearBtn);
  }
}

function kbdRemoveFilterTag(tag) {
  const sidebar = document.getElementById('filter-sidebar');
  if (!sidebar) return;
  const cb = sidebar.querySelector('input[type="checkbox"][data-filter-tag="' + tag + '"]');
  if (cb) cb.checked = false;
  const sw = sidebar.querySelector('.color-swatch[data-filter-tag="' + tag + '"]');
  if (sw) sw.classList.remove('is-selected');
  kbdApplyFilters();
}

function kbdClearAllFilters() {
  const sidebar = document.getElementById('filter-sidebar');
  if (!sidebar) return;
  sidebar.querySelectorAll('input[type="checkbox"]').forEach(cb => cb.checked = false);
  sidebar.querySelectorAll('.color-swatch').forEach(sw => sw.classList.remove('is-selected'));
  sidebar.querySelectorAll('input[type="number"]').forEach(i => i.value = '');
  if (typeof window.kbdResetPriceSlider === 'function') window.kbdResetPriceSlider();
  kbdApplyFilters();
}

// Dual-handle price slider: two overlaid <input type="range"> elements with
// pointer-events: none on the tracks and auto on the thumbs. Text inputs
// below mirror the handle values; typing in either input moves its handle.
// Handles can't cross. Empty text input = that side of the range is inactive
// (so kbdGetActiveFilters reads null, matching existing behavior).
function kbdInitPriceSlider() {
  const sidebar = document.getElementById('filter-sidebar');
  if (!sidebar) return;
  const rMin = sidebar.querySelector('[data-filter-role="price-range-min"]');
  const rMax = sidebar.querySelector('[data-filter-role="price-range-max"]');
  const nMin = sidebar.querySelector('[data-filter-role="price-min"]');
  const nMax = sidebar.querySelector('[data-filter-role="price-max"]');
  const fill = sidebar.querySelector('[data-filter-role="price-fill"]');
  if (!rMin || !rMax || !nMin || !nMax || !fill) return;

  const rangeMin = Number(rMin.min);
  const rangeMax = Number(rMax.max);

  function updateFill() {
    const lo = Number(rMin.value);
    const hi = Number(rMax.value);
    const span = rangeMax - rangeMin || 1;
    fill.style.left = (((lo - rangeMin) / span) * 100) + '%';
    fill.style.right = (((rangeMax - hi) / span) * 100) + '%';
  }

  function liftMin() { rMin.style.zIndex = 3; rMax.style.zIndex = 2; }
  function liftMax() { rMin.style.zIndex = 2; rMax.style.zIndex = 3; }

  function handleRangeMin() {
    let lo = Number(rMin.value);
    const hi = Number(rMax.value);
    if (lo > hi) { lo = hi; rMin.value = lo; }
    nMin.value = (lo === rangeMin) ? '' : lo;
    liftMin();
    updateFill();
    kbdApplyFilters();
  }

  function handleRangeMax() {
    const lo = Number(rMin.value);
    let hi = Number(rMax.value);
    if (hi < lo) { hi = lo; rMax.value = hi; }
    nMax.value = (hi === rangeMax) ? '' : hi;
    liftMax();
    updateFill();
    kbdApplyFilters();
  }

  function handleInputMin() {
    const raw = nMin.value;
    let v = (raw === '' || isNaN(Number(raw))) ? rangeMin : Number(raw);
    v = Math.max(rangeMin, Math.min(rangeMax, v));
    const hi = Number(rMax.value);
    if (v > hi) v = hi;
    rMin.value = v;
    updateFill();
    kbdApplyFilters();
  }

  function handleInputMax() {
    const raw = nMax.value;
    let v = (raw === '' || isNaN(Number(raw))) ? rangeMax : Number(raw);
    v = Math.max(rangeMin, Math.min(rangeMax, v));
    const lo = Number(rMin.value);
    if (v < lo) v = lo;
    rMax.value = v;
    updateFill();
    kbdApplyFilters();
  }

  rMin.addEventListener('input', handleRangeMin);
  rMax.addEventListener('input', handleRangeMax);
  rMin.addEventListener('mousedown', liftMin);
  rMax.addEventListener('mousedown', liftMax);
  rMin.addEventListener('touchstart', liftMin, { passive: true });
  rMax.addEventListener('touchstart', liftMax, { passive: true });
  nMin.addEventListener('input', handleInputMin);
  nMax.addEventListener('input', handleInputMax);

  window.kbdResetPriceSlider = function () {
    rMin.value = rangeMin;
    rMax.value = rangeMax;
    nMin.value = '';
    nMax.value = '';
    liftMax();
    updateFill();
  };

  updateFill();
}

// Wire sidebar state changes to re-apply filters.
// Color swatches use the existing click handler that toggles .is-selected — re-apply on tick delay.
// Demo-only filter wiring. The real collection page uses the IIFE inside
// sections/collection.liquid (canonical-color logic + image/label sync).
// On real Shopify (window.Shopify defined) we do NOT want the legacy
// kbdApplyFilters delegated handler attached: it races the IIFE, sets
// card.style.display directly (clobbering the .filtered-out class), and
// can't update card labels because it relies on KBD_PRODUCTS_BY_HANDLE
// which only exists in the static demo. The static index.html still
// benefits from the legacy path, so we only skip on Shopify.
function initFilters() {
  if (typeof window.Shopify !== 'undefined') return;
  const sidebar = document.getElementById('filter-sidebar');
  if (!sidebar || sidebar.dataset.kbdInit === '1') return;
  sidebar.dataset.kbdInit = '1';
  sidebar.addEventListener('change', (e) => {
    if (e.target && e.target.matches('input')) kbdApplyFilters();
  });
  sidebar.addEventListener('click', (e) => {
    if (e.target && e.target.classList && e.target.classList.contains('color-swatch')) {
      setTimeout(kbdApplyFilters, 0);
    }
  });
}

// ═══════════════════════════════════════════════════════════════════
// COLLECTION SORT — reorder grid children by the dropdown selection.
// Prices come from the data-price attribute (cents) on each card's
// price element. Featured / Best Selling / Newest First all restore
// the KBD_PRODUCTS source-of-truth order from PRODUCTS.json.
// Sort reorders every card; filter-hidden cards stay hidden, so the
// visible count above the grid remains accurate.
// ═══════════════════════════════════════════════════════════════════
function initSort() {
  const select = document.querySelector('.sort-dropdown');
  const grid = document.getElementById('collection-grid');
  if (!select || !grid || select.dataset.kbdInit === '1') return;
  select.dataset.kbdInit = '1';

  const originalIndex = {};
  KBD_PRODUCTS.forEach((p, i) => { originalIndex[p.handle] = i; });

  function priceOf(card) {
    const el = card.querySelector('.product-card__price');
    return el ? Number(el.getAttribute('data-price') || 0) : 0;
  }
  function origIdx(card) {
    const i = originalIndex[card.getAttribute('data-product-handle')];
    return i === undefined ? Number.MAX_SAFE_INTEGER : i;
  }

  select.addEventListener('change', (e) => {
    const mode = e.target.value;
    const cards = Array.from(grid.children);
    let cmp;
    if (mode === 'Price: Low to High') cmp = (a, b) => priceOf(a) - priceOf(b);
    else if (mode === 'Price: High to Low') cmp = (a, b) => priceOf(b) - priceOf(a);
    else cmp = (a, b) => origIdx(a) - origIdx(b);
    cards.sort(cmp).forEach(c => grid.appendChild(c));
  });
}

// NAV TRANSPARENCY — formerly toggled .nav--transparent / .nav--scrolled
// based on scroll + URL so the homepage hero could bleed under a transparent
// header. The nav is now always solid #0D0D0D, so these stubs exist only to
// keep the bootstrap call sites stable and to defensively scrub any class
// the previous build may have persisted on the cached header DOM.
function initNavTransparency() {
  const header = document.querySelector('.header');
  if (!header) return;
  header.classList.remove('nav--transparent', 'nav--scrolled', 'nav--solid');
}

function updateNavTransparentByUrl() {
  const header = document.querySelector('.header');
  if (!header) return;
  header.classList.remove('nav--transparent', 'nav--scrolled', 'nav--solid');
}

// SEARCH BAR — slim drop-down that slides in below the nav, with
// predictive search powered by Shopify's /search/suggest.json endpoint.
// As the user types (>=2 chars, debounced 150ms) we fetch product +
// article suggestions and render them in a sibling .search-bar__results
// dropdown. Click a result -> navigate to that URL. Enter with a typed
// query but no result clicked -> /search?q=<query>. Esc / outside-click
// closes the dropdown. Globals exposed: kbdToggleSearch, kbdCloseSearch.
function initSearchOverlay() {
  const bar = document.getElementById('searchBar');
  if (!bar || bar.dataset.kbdInit === '1') return;
  bar.dataset.kbdInit = '1';
  const input   = document.getElementById('search-bar-input');
  const form    = bar.querySelector('form');
  const results = document.getElementById('search-bar-results');

  function open() {
    bar.classList.add('is-open');
    bar.setAttribute('aria-hidden', 'false');
    setTimeout(() => { if (input) input.focus(); }, 30);
  }
  function close() {
    bar.classList.remove('is-open');
    bar.setAttribute('aria-hidden', 'true');
    closeResults();
    if (input) input.blur();
  }
  function openResults() {
    if (!results) return;
    results.classList.add('is-open');
    results.setAttribute('aria-hidden', 'false');
  }
  function closeResults() {
    if (!results) return;
    results.classList.remove('is-open');
    results.setAttribute('aria-hidden', 'true');
    results.innerHTML = '';
  }

  window.kbdToggleSearch = function() {
    if (bar.classList.contains('is-open')) close();
    else open();
  };
  window.kbdCloseSearch = close;
  window.kbdOpenSearch  = open;

  // Esc: close results first, then bar on a second press.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (results && results.classList.contains('is-open')) closeResults();
    else if (bar.classList.contains('is-open')) close();
  });

  // Click outside the bar/results closes the dropdown only (leaves bar open).
  document.addEventListener('click', (e) => {
    if (bar.contains(e.target)) return;
    if (results && results.contains(e.target)) return;
    closeResults();
  });

  // ----- Predictive search via /search/suggest.json -----
  let debounceTimer = null;
  let activeController = null;

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
  function getProductImage(p) {
    if (p.featured_image && p.featured_image.url) return p.featured_image.url;
    if (typeof p.image === 'string' && p.image) return p.image;
    if (p.image && p.image.url) return p.image.url;
    return '';
  }
  function getProductPrice(p) {
    // /search/suggest.json returns price either as a number (cents) or
    // a pre-formatted shop-currency string. The string form sometimes
    // omits the currency symbol depending on the locale, so we always
    // prepend $ if no currency glyph is present.
    let raw = '';
    if (typeof p.price === 'number') {
      raw = (p.price / 100).toFixed(2);
    } else if (typeof p.price === 'string') {
      raw = p.price;
    } else if (p.price_min) {
      raw = String(p.price_min);
    }
    if (!raw) return '';
    if (/[\$€£¥]/.test(raw)) return raw;
    return '$' + raw;
  }

  function render(data) {
    if (!results) return;
    const r = (data && data.resources && data.resources.results) || {};
    const products = r.products || [];
    const articles = r.articles || [];
    if (products.length === 0 && articles.length === 0) {
      results.innerHTML = '<div class="search-bar__empty">No results.</div>';
      openResults();
      return;
    }
    let html = '';
    if (products.length) {
      html += '<div class="search-bar__group">';
      html += '<div class="search-bar__group-label">Products</div>';
      products.forEach((p) => {
        const img = getProductImage(p);
        const price = getProductPrice(p);
        html += '<a class="search-bar__result search-bar__result--product" href="' + escapeHtml(p.url) + '">';
        html += '<span class="search-bar__thumb">';
        if (img) html += '<img src="' + escapeHtml(img) + '" alt="" loading="lazy">';
        html += '</span>';
        html += '<span class="search-bar__result-body">';
        html += '<span class="search-bar__result-title">' + escapeHtml(p.title) + '</span>';
        if (price) html += '<span class="search-bar__result-meta">' + escapeHtml(price) + '</span>';
        html += '</span></a>';
      });
      html += '</div>';
    }
    if (articles.length) {
      html += '<div class="search-bar__group">';
      html += '<div class="search-bar__group-label">Journal</div>';
      articles.forEach((a) => {
        html += '<a class="search-bar__result search-bar__result--article" href="' + escapeHtml(a.url) + '">';
        html += '<span class="search-bar__result-body">';
        html += '<span class="search-bar__result-eyebrow">Journal</span>';
        html += '<span class="search-bar__result-title">' + escapeHtml(a.title) + '</span>';
        html += '</span></a>';
      });
      html += '</div>';
    }
    results.innerHTML = html;
    openResults();
  }

  function fetchSuggestions(q) {
    if (activeController) activeController.abort();
    activeController = new AbortController();
    const url = '/search/suggest.json'
      + '?q=' + encodeURIComponent(q)
      + '&resources[type]=product,article'
      + '&resources[limit]=5';
    fetch(url, { signal: activeController.signal, headers: { 'Accept': 'application/json' } })
      .then((res) => res.ok ? res.json() : Promise.reject(new Error('http ' + res.status)))
      .then(render)
      .catch((err) => {
        if (err && err.name === 'AbortError') return;
        // On any other failure, just hide the dropdown rather than show errors.
        closeResults();
      });
  }

  if (input) {
    input.addEventListener('input', () => {
      const q = input.value.trim();
      if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
      if (q.length < 2) {
        if (activeController) { activeController.abort(); activeController = null; }
        closeResults();
        return;
      }
      debounceTimer = setTimeout(() => fetchSuggestions(q), 150);
    });
  }

  // Result click: visually close the bar before the anchor commits navigation.
  // We only flip the class — innerHTML stays intact so the click event isn't
  // disrupted mid-flight (the header lives outside #swup so it persists).
  if (results) {
    results.addEventListener('click', (e) => {
      const a = e.target.closest('a.search-bar__result');
      if (!a) return;
      bar.classList.remove('is-open');
      bar.setAttribute('aria-hidden', 'true');
      results.classList.remove('is-open');
      results.setAttribute('aria-hidden', 'true');
    });
  }

  if (form) {
    form.addEventListener('submit', (e) => {
      // Form has no action attribute; intercept and route to /search?q=...
      // Only navigate when there's a typed query — empty submit is a no-op.
      e.preventDefault();
      const q = input ? input.value.trim() : '';
      if (!q) return;
      close();
      window.location.href = '/search?q=' + encodeURIComponent(q);
    });
  }
}

// FAQ ACCORDION — stored page content has no onclick attrs (they were
// stripped during sanitize), so we delegate clicks here. Registered early
// so it lands regardless of later throws, and uses readyState/DOMContent-
// Loaded to cover both script-load timings.
function initFaqAccordion() {
  const faq = document.querySelector('.faq');
  if (!faq || faq.dataset.kbdInit === '1') return;
  faq.dataset.kbdInit = '1';
  faq.addEventListener('click', (e) => {
    const q = e.target.closest('.faq-item__q');
    if (!q || !faq.contains(q)) return;
    e.preventDefault();
    const item = q.closest('.faq-item');
    if (!item) return;
    const wasOpen = item.classList.contains('is-open');
    // Single-open rule: close every item in the FAQ first…
    faq.querySelectorAll('.faq-item.is-open').forEach(o => o.classList.remove('is-open'));
    // …then reopen the clicked one unless it was already open (toggle).
    if (!wasOpen) item.classList.add('is-open');
  });
}

// PDP GALLERY — vertical thumb strip, arrow nav, swipe, trackpad wheel.
// Max 7 thumbnails rendered in the DOM; images beyond #7 are navigated via
// arrows/swipe using the full URL list in kbdGalleryState.allImages.
// State is shared between initPdpGallery (init) and kbdRebuildPdpGallery
// (color-swatch rebuild) so arrow nav always reflects the current variant.
const kbdGalleryState = { allImages: [], curIdx: 0 };

// Rebuild the PDP thumb strip + main image when a color swatch is clicked.
// Reads a JSON array of {thumb, main} URL pairs that Liquid writes onto each
// swatch's data-variant-images attribute. The array comes from the variant's
// custom.variant_gallery metafield (with fallbacks to variant.image and
// product.images). Replaces #pdp-thumbs.innerHTML so the existing delegated
// click handler in initPdpGallery() automatically picks up the new thumbs —
// no relisten or rebind required.
function kbdRebuildPdpGallery(jsonStr) {
  if (!jsonStr) return;
  let images;
  try { images = JSON.parse(jsonStr); } catch (e) { return; }
  if (!Array.isArray(images) || images.length === 0) return;

  const thumbsEl = document.getElementById('pdp-thumbs');
  const mainImg  = document.getElementById('pdp-main-image');
  if (!thumbsEl || !mainImg) return;

  // Update shared state — full list for arrow nav beyond the 7 visible thumbs
  kbdGalleryState.allImages = images.map(img => img.main || img.thumb || '').filter(Boolean);
  kbdGalleryState.curIdx = 0;

  // Render first 7 thumbs only
  const frag = document.createDocumentFragment();
  images.slice(0, 7).forEach((img, i) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'pdp__thumb' + (i === 0 ? ' is-active' : '');
    b.setAttribute('data-src', img.main || '');
    b.setAttribute('aria-label', 'Show image ' + (i + 1) + ' of ' + images.length);
    if (images.length > 7 && i === 6) b.setAttribute('data-overflow', images.length - 7);
    const im = document.createElement('img');
    im.src = img.thumb || img.main || '';
    im.alt = (mainImg.alt || 'Product') + ', photo ' + (i + 1);
    im.loading = 'lazy';
    b.appendChild(im);
    frag.appendChild(b);
  });
  thumbsEl.innerHTML = '';
  thumbsEl.appendChild(frag);

  if (images[0] && images[0].main) {
    mainImg.src = images[0].main;
  }
}

function initPdpGallery() {
  const gallery  = document.getElementById('pdp-gallery');
  if (!gallery || gallery.dataset.kbdInit === '1') return;
  gallery.dataset.kbdInit = '1';
  const mainImg  = document.getElementById('pdp-main-image');
  const thumbsEl = document.getElementById('pdp-thumbs');
  const prevBtn  = document.getElementById('pdp-nav-prev');
  const nextBtn  = document.getElementById('pdp-nav-next');
  const mainWrap = gallery.querySelector('.pdp__main-wrap');
  if (!mainImg || !thumbsEl) return;

  // Ensure main image has a src on init
  (function ensureMainImageSrc() {
    const cur = mainImg.getAttribute('src');
    if (cur && cur.length > 0) return;
    const firstThumb = thumbsEl.querySelector('.pdp__thumb');
    if (firstThumb) {
      const ds = firstThumb.getAttribute('data-src');
      if (ds) { mainImg.src = ds; return; }
      const fimg = firstThumb.querySelector('img');
      if (fimg && fimg.src) { mainImg.src = fimg.src; return; }
    }
    const fallback = mainImg.getAttribute('data-fallback-src');
    if (fallback) mainImg.src = fallback;
  })();

  // Load full image list from Liquid-embedded JSON (all images, not just 7 thumbs)
  const jsonEl = document.getElementById('pdp-all-images-json');
  if (jsonEl) {
    try {
      const parsed = JSON.parse(jsonEl.textContent);
      if (Array.isArray(parsed) && parsed.length) kbdGalleryState.allImages = parsed;
    } catch (e) {}
  }
  // Fallback: read from thumb data-srcs if JSON block absent
  if (!kbdGalleryState.allImages.length) {
    kbdGalleryState.allImages = Array.from(thumbsEl.querySelectorAll('.pdp__thumb'))
      .map(t => t.getAttribute('data-src') || '')
      .filter(Boolean);
  }
  kbdGalleryState.curIdx = 0;

  // Navigate to image at index idx (wraps). Highlights the matching thumb only
  // for images 0–6; navigating to image 7+ leaves all thumbs unhighlighted.
  function goto(idx) {
    const total = kbdGalleryState.allImages.length;
    if (!total) return;
    const next = ((idx % total) + total) % total;
    kbdGalleryState.curIdx = next;
    const src = kbdGalleryState.allImages[next];
    // W3-4: a user swap targets a specific single URL; drop the LCP srcset
    // (which pins the initial image) so the new src actually paints.
    if (src) { mainImg.removeAttribute('srcset'); mainImg.src = src; }
    const ts = Array.from(thumbsEl.querySelectorAll('.pdp__thumb'));
    ts.forEach(t => t.classList.remove('is-active'));
    if (next < ts.length) ts[next].classList.add('is-active');
    // No scrollIntoView — 7 thumbs fit without scrolling
  }

  // Thumb clicks — delegated so rebuilt thumbs keep working after swatch change.
  // If the thumb has data-overflow (the "+X more" overlay), open the lightbox
  // at the first hidden image (idx + 1) instead of switching the main view.
  thumbsEl.addEventListener('click', (e) => {
    const t = e.target.closest('.pdp__thumb');
    if (!t || !thumbsEl.contains(t)) return;
    e.preventDefault();
    const idx = Array.from(thumbsEl.querySelectorAll('.pdp__thumb')).indexOf(t);
    if (idx < 0) return;
    if (t.hasAttribute('data-overflow')) {
      lbOpen(idx);
    } else {
      goto(idx);
    }
  });

  // Arrow nav — wraps at both ends
  if (prevBtn) prevBtn.addEventListener('click', (e) => { e.preventDefault(); goto(kbdGalleryState.curIdx - 1); });
  if (nextBtn) nextBtn.addEventListener('click', (e) => { e.preventDefault(); goto(kbdGalleryState.curIdx + 1); });

  // Touch swipe on the main image area
  let touchStartX = null;
  let touchStartY = null;
  if (mainWrap) {
    mainWrap.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) { touchStartX = null; return; }
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { passive: true });
    mainWrap.addEventListener('touchend', (e) => {
      if (touchStartX == null) return;
      const endX = (e.changedTouches[0] || {}).clientX;
      const endY = (e.changedTouches[0] || {}).clientY;
      const dx = endX - touchStartX;
      const dy = endY - touchStartY;
      touchStartX = touchStartY = null;
      if (Math.abs(dx) < 40) return;
      if (Math.abs(dy) > Math.abs(dx)) return;
      goto(kbdGalleryState.curIdx + (dx < 0 ? 1 : -1));
    }, { passive: true });
  }

  // Trackpad horizontal wheel — throttled
  let wheelLock = false;
  if (mainWrap) {
    mainWrap.addEventListener('wheel', (e) => {
      if (Math.abs(e.deltaX) < 30) return;
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) return;
      if (wheelLock) { e.preventDefault(); return; }
      e.preventDefault();
      wheelLock = true;
      goto(kbdGalleryState.curIdx + (e.deltaX > 0 ? 1 : -1));
      setTimeout(() => { wheelLock = false; }, 400);
    }, { passive: false });
  }

  // ── Lightbox ──────────────────────────────────────────────────────────────
  let lb = document.getElementById('pdp-lightbox');
  if (!lb) {
    lb = document.createElement('div');
    lb.id = 'pdp-lightbox';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.innerHTML =
      '<button class="pdp-lb__btn pdp-lb__prev" aria-label="Previous image">&#x2039;</button>' +
      '<img id="pdp-lightbox-img" src="" alt="">' +
      '<button class="pdp-lb__btn pdp-lb__next" aria-label="Next image">&#x203A;</button>' +
      '<button class="pdp-lb__btn pdp-lb__close" aria-label="Close">&#x2715;</button>' +
      '<div class="pdp-lb__counter" id="pdp-lb-counter"></div>';
    document.body.appendChild(lb);
  }
  const lbImg     = document.getElementById('pdp-lightbox-img');
  const lbCounter = document.getElementById('pdp-lb-counter');
  const lbPrevBtn = lb.querySelector('.pdp-lb__prev');
  const lbNextBtn = lb.querySelector('.pdp-lb__next');
  const lbCloseBtn = lb.querySelector('.pdp-lb__close');
  let lbIsOpen = false;

  function lbGoto(idx) {
    const total = kbdGalleryState.allImages.length;
    if (!total) return;
    const i = ((idx % total) + total) % total;
    kbdGalleryState.curIdx = i;
    lbImg.src = kbdGalleryState.allImages[i];
    if (lbCounter) lbCounter.textContent = (i + 1) + ' / ' + total;
  }

  function lbOpen(idx) {
    lbGoto(idx);
    lb.classList.add('is-open');
    lbIsOpen = true;
    document.body.style.overflow = 'hidden';
  }

  function lbClose() {
    lb.classList.remove('is-open');
    lbIsOpen = false;
    document.body.style.overflow = '';
    // Sync main gallery view to wherever lightbox left off
    const src = kbdGalleryState.allImages[kbdGalleryState.curIdx];
    if (src) { mainImg.removeAttribute('srcset'); mainImg.src = src; }
    const ts = Array.from(thumbsEl.querySelectorAll('.pdp__thumb'));
    ts.forEach(t => t.classList.remove('is-active'));
    if (kbdGalleryState.curIdx < ts.length) ts[kbdGalleryState.curIdx].classList.add('is-active');
  }

  // Open on main image click
  mainImg.addEventListener('click', () => { lbOpen(kbdGalleryState.curIdx); });

  lbCloseBtn.addEventListener('click', lbClose);
  lbPrevBtn.addEventListener('click', () => { lbGoto(kbdGalleryState.curIdx - 1); });
  lbNextBtn.addEventListener('click', () => { lbGoto(kbdGalleryState.curIdx + 1); });

  // Click backdrop to close
  lb.addEventListener('click', (e) => { if (e.target === lb) lbClose(); });

  // Keyboard nav
  document.addEventListener('keydown', (e) => {
    if (!lbIsOpen) return;
    if (e.key === 'Escape')      lbClose();
    else if (e.key === 'ArrowLeft')  lbGoto(kbdGalleryState.curIdx - 1);
    else if (e.key === 'ArrowRight') lbGoto(kbdGalleryState.curIdx + 1);
  });

  // Touch swipe inside lightbox
  let lbTouchX = null;
  lb.addEventListener('touchstart', (e) => {
    lbTouchX = e.touches.length === 1 ? e.touches[0].clientX : null;
  }, { passive: true });
  lb.addEventListener('touchend', (e) => {
    if (lbTouchX == null) return;
    const dx = (e.changedTouches[0] || {}).clientX - lbTouchX;
    lbTouchX = null;
    if (Math.abs(dx) < 40) return;
    lbGoto(kbdGalleryState.curIdx + (dx < 0 ? 1 : -1));
  }, { passive: true });
}

// CART WIRING — delegated, capture-phase click handlers for all add-to-cart
// surfaces. Runs before any inline onclick the mockup may have assigned, so
// a stopImmediatePropagation() here prevents the legacy mock handlers from
// firing with a product handle. Reads variant ids from the DOM (placed by
// Liquid on swatches / cards / quick-add buttons).
function initCartWiring() {
  if (document.body.dataset.kbdCartWired === '1') return;
  document.body.dataset.kbdCartWired = '1';

  async function handlePdpCta(btn, e) {
    const pdp = btn.closest('[data-page="pdp"]') || document;
    const selected = pdp.querySelector('#pdp-swatches .color-swatch.is-selected[data-variant-id]')
                  || pdp.querySelector('#pdp-swatches .color-swatch[data-variant-id]');
    const variantId = (selected && selected.getAttribute('data-variant-id'))
                   || pdp.getAttribute('data-variant-id')
                   || btn.getAttribute('data-variant-id');
    if (!variantId) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    const qtyEl   = pdp.querySelector('.pdp__qty-val');
    const qtyWrap  = pdp.querySelector('#pdp-qty-wrap');
    const rawQty   = qtyEl ? parseInt(qtyEl.value, 10) || 1 : 1;
    const maxQty   = qtyWrap ? parseInt(qtyWrap.getAttribute('data-max-qty'), 10) : NaN;
    const qty      = !isNaN(maxQty) && maxQty > 0
                     ? Math.min(Math.max(1, rawQty), maxQty)
                     : Math.max(1, rawQty);
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.classList.add('is-loading');
    btn.textContent = 'Adding…';
    const ok = await kbdCartAdd(variantId, qty);
    btn.classList.remove('is-loading');
    if (ok) {
      btn.classList.add('is-added');
      btn.textContent = 'Added ✓';
      if (qtyEl) {
        qtyEl.value = '1';
        const incBtn = pdp.querySelector('#pdp-qty-inc');
        if (incBtn) incBtn.disabled = !isNaN(maxQty) && maxQty > 0 && maxQty <= 1;
      }
      setTimeout(() => {
        btn.classList.remove('is-added');
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1100);
    } else {
      btn.textContent = originalText;
      btn.disabled = false;
    }
  }

  async function handleQuickAdd(btn, e) {
    const card = btn.closest('.product-card');
    const variantId = btn.getAttribute('data-variant-id')
                   || (card && card.getAttribute('data-variant-id'));
    if (!variantId) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    btn.disabled = true;
    const wasText = btn.textContent;
    btn.textContent = '…';
    const ok = await kbdCartAdd(variantId, 1);
    btn.disabled = false;
    btn.textContent = wasText;
    if (ok) {
      btn.classList.add('is-added');
      setTimeout(() => btn.classList.remove('is-added'), 900);
    }
  }

  document.addEventListener('click', (e) => {
    const pdpCta = e.target.closest('#pdp-cta');
    if (pdpCta) { handlePdpCta(pdpCta, e); return; }
    const quickAdd = e.target.closest('.product-card__quick-add');
    if (quickAdd) { handleQuickAdd(quickAdd, e); return; }
  }, true); // capture phase so we pre-empt inline onclick handlers
}

// QUIZ DATA — hoisted above the throwing render calls further down so that
// setupQuiz (and renderQuizStep called from it) can safely read them.
const quizQuestions = [
  {
    question: "Where are you in your exploration?",
    options: [
      { label: "New to this: I've never used bondage accessories", value: "beginner" },
      { label: "Curious and somewhat experienced: we've tried a few things", value: "curious" },
      { label: "Experienced: we know what we like", value: "experienced" },
      { label: "Advanced: looking to expand a deeper practice", value: "advanced" }
    ]
  },
  {
    question: "Who are you shopping for?",
    options: [
      { label: "Myself and one partner", value: "couple" },
      { label: "Myself, solo", value: "solo" },
      { label: "A gift for someone else", value: "gift" },
      { label: "Not sure yet: just browsing", value: "browse" }
    ]
  },
  {
    question: "What draws you most?",
    options: [
      { label: "Restraint and tying", value: "restraint" },
      { label: "Sensory play: blindfolds, texture, impact", value: "sensory" },
      { label: "Dominance and power dynamics", value: "power" },
      { label: "Full immersion: jackets, full-body", value: "immersion" }
    ]
  },
  {
    question: "How intense do you want it?",
    options: [
      { label: "Soft and gentle", value: "soft" },
      { label: "Firm but forgiving", value: "firm" },
      { label: "Intense and immersive", value: "intense" }
    ]
  },
  {
    question: "What's your comfort range?",
    options: [
      { label: "Under $40", value: "low" },
      { label: "$40 – $80", value: "mid" },
      { label: "$80 – $150", value: "high" },
      { label: "$150+", value: "premium" }
    ]
  }
];

let quizState = {
  currentStep: 0,
  answers: []
};

// answers indices: 0=experience, 1=who, 2=style, 3=intensity, 4=price
function pickQuizProduct(answers) {
  const exp       = answers[0]; // beginner curious experienced advanced
  const style     = answers[2]; // restraint sensory power immersion
  const intensity = answers[3]; // soft firm intense
  const price     = answers[4]; // low mid high premium
  const highPrice = price === 'high' || price === 'premium';
  const expHigh   = exp === 'experienced' || exp === 'advanced';

  // 1. Under $40 → always 5pc
  if (price === 'low') return '5pc';

  // 2. New → 10pc
  if (exp === 'beginner') return '10pc';

  // 3. Sensory OR (restraint + intense) + experienced/advanced → whip
  if ((style === 'sensory' || (style === 'restraint' && intensity === 'intense')) && expHigh) return 'whip';

  // 4. Full immersion
  if (style === 'immersion') {
    if (expHigh && highPrice) return 'jacket';
    return 'half-jacket';
  }

  // 5. Restraint and tying
  if (style === 'restraint') {
    if (exp === 'curious' || exp === 'experienced') return '5pc';
    return '10pc';
  }

  // 6. Dominance and power dynamics
  if (style === 'power') {
    if (exp === 'advanced') return 'jacket';
    if (exp === 'experienced' && highPrice) return '15pc';
    return '10pc';
  }

  // 7. Default fallback
  if (highPrice) return '15pc';
  return '10pc';
}

const QUIZ_PRODUCT_META = {
  '5pc':        { handle: 'bed-restraint-5-piece', eyebrow: 'Restraint Kit · Starter',        label: 'Bed Restraint Kit (5 Piece)' },
  '10pc':       { handle: '10-piece-standard-set', eyebrow: 'Restraint Kit · Starter',        label: 'The Standard (10 Piece)' },
  '15pc':       { handle: '15-piece-set',          eyebrow: 'Complete Kit · Intermediate',    label: 'The Complete (15 Piece)' },
  'whip':       { handle: '8-piece-whip-set',      eyebrow: 'Impact Play · Advanced',         label: 'The Impact (8 Piece)' },
  'jacket':     { handle: 'bondage-jacket-m',      eyebrow: 'Bondage Jacket · Advanced',      label: 'Bondage Jacket' },
  'half-jacket':{ handle: 'half-jacket-s',         eyebrow: 'Jacket · Intermediate',          label: 'Half Jacket' }
};

const QUIZ_RATIONALE = {
  '5pc': {
    default:  'Simple, considered, and built for a first experience — the 5-piece kit covers the essentials without overwhelming. Adjust, explore, and build from here.',
    couple:   'Designed for two. The 5-piece covers the core of restraint play without overcomplicating a first shared experience.',
    solo:     'A focused starting point for solo exploration. Everything you need, nothing you don\'t.',
    gift:     'Approachable enough to gift without guesswork — this 5-piece kit is a considered starting point for someone curious.',
    browse:   'If you\'re not sure where to start, this 5-piece is the most forgiving entry point in the catalogue.'
  },
  '10pc': {
    default:  'Our most recommended starting point. The 10-piece has the breadth to grow into — restraint, texture, and sensation — without assuming too much.',
    couple:   'Broad enough for two people still figuring out what they like. The 10-piece gives you room to explore together.',
    solo:     'Ten pieces gives you the flexibility to explore at your own pace. Start with one or two and build from there.',
    gift:     'A complete first kit — thoughtful as a gift, easy to start with, and worth coming back to.',
    browse:   'The Standard is where most people begin. It covers the range without committing to a direction you\'re not sure about yet.'
  },
  '15pc': {
    default:  'You know what you want. The 15-piece is built for people who\'ve moved past \'starter\' and want variety in a single kit.',
    couple:   'For couples with a clear sense of what draws them. This kit handles a full session without reaching for something else.',
    solo:     'A complete solo kit for someone who\'s already done the groundwork.',
    gift:     'A premium gift for someone with experience — this is the kit they\'ll actually use.',
    browse:   'If you\'re browsing seriously, the 15-piece is the most complete set in the restraint category.'
  },
  'whip': {
    default:  'You\'re past restraint and into rhythm. The 8-piece impact kit is built for experienced play — whips, floggers, and the tools for a considered scene.',
    couple:   'For couples with an established dynamic. This kit is for the scenes you\'ve already been building toward.',
    solo:     'A focused impact kit for someone who knows what they\'re after.',
    gift:     'A specialist gift — best for someone already comfortable with impact play.',
    browse:   'If sensory and impact are where your interest lives, this is the right category.'
  },
  'jacket': {
    default:  'Full immersion, built properly. The bondage jacket is for established practice — structured, wearable, and designed for longer sessions.',
    couple:   'For couples with a deep and established dynamic. This is a committed piece, not a starting point.',
    solo:     'A wearable piece for solo sensory immersion. Built for the kind of practice you\'ve spent time developing.',
    gift:     'A serious gift for someone with a serious practice. Not a casual recommendation.',
    browse:   'The jacket is the furthest end of the immersion spectrum — worth knowing about even if you\'re not ready yet.'
  },
  'half-jacket': {
    default:  'The entry point into jacket-style play. Structured enough to feel immersive, approachable enough to start with.',
    couple:   'A good first jacket for couples curious about full-body restraint — without the full commitment of a complete jacket.',
    solo:     'A wearable introduction to immersive solo play. Structured but not overwhelming.',
    gift:     'Thoughtful for someone who\'s shown interest in jacket-style restraint but hasn\'t taken the step yet.',
    browse:   'If jackets interest you but you\'re not sure about full immersion, the half jacket is the right place to start.'
  }
};

function buildQuizRationale(key, who, intensity) {
  const pool = QUIZ_RATIONALE[key] || QUIZ_RATIONALE['10pc'];
  let base = pool[who] || pool.default;
  let suffix = '';
  if (intensity === 'soft')   suffix = ' A considered start.';
  if (intensity === 'firm')   suffix = ' Built for committed play.';
  if (intensity === 'intense') suffix = ' Suited for immersive experiences.';
  return base + suffix;
}

function populateQuizResult(answers) {
  const key  = pickQuizProduct(answers);
  const meta = QUIZ_PRODUCT_META[key];
  const who  = answers[1];      // couple solo gift browse
  const intensity = answers[3]; // soft firm intense

  const dataEl = document.getElementById('kbdQuizProducts');
  let productData = {};
  if (dataEl) {
    try { productData = JSON.parse(dataEl.textContent); } catch (e) {}
  }
  const p = productData[meta.handle] || {};
  const title    = p.title || meta.label;
  const imageUrl = p.image || '';
  const price    = p.price ? ('$' + (p.price / 100).toFixed(2)) : '';
  const url      = p.url  || ('/products/' + meta.handle);
  const rationale = buildQuizRationale(key, who, intensity);

  const imgEl = document.getElementById('quizResultImg');
  if (imgEl) {
    imgEl.innerHTML = imageUrl
      ? `<img src="${imageUrl}" alt="${title.replace(/"/g, '&quot;')}" style="width:100%;height:100%;object-fit:cover;display:block;">`
      : '';
  }

  const contentEl = document.getElementById('quizResultContent');
  if (contentEl) {
    contentEl.innerHTML = `
      <div class="eyebrow" style="margin-bottom:12px;">${meta.eyebrow}</div>
      <h2 style="margin-bottom:16px;font-size:36px;">${title}</h2>
      <div class="featured-result__rationale">${rationale}</div>
      <div style="font-family:var(--font-body);font-size:28px;margin-bottom:24px;">${price}</div>
      <div style="display:flex;gap:12px;flex-wrap:wrap;">
        <a href="${url}" class="btn btn--primary">Shop the kit</a>
        <a href="${url}" class="btn btn--secondary">Learn more</a>
      </div>`;
  }
}

// QUIZ — initialize once if the quiz DOM is present on this page. Registered
// early (before the throwing top-level render calls) so the script can't
// halt before the listener is attached. Also rebinds the Next/Back/Retake
// click handlers that were stripped out when the page body was sanitized.
function initQuiz() {
  const quizStep = document.getElementById('quizStep');
  if (!quizStep || quizStep.dataset.kbdInit === '1') return;
  quizStep.dataset.kbdInit = '1';
  // renderQuizStep (function declaration, hoisted) builds the option
  // buttons and attaches their click handlers. Safe to call now because
  // quizQuestions/quizState are initialized just above.
  if (typeof renderQuizStep === 'function') renderQuizStep();
  const nextBtn = document.getElementById('quizNext');
  if (nextBtn && typeof quizNext === 'function') nextBtn.addEventListener('click', quizNext);
  const backBtn = document.querySelector('.quiz__back');
  if (backBtn && typeof quizBack === 'function') backBtn.addEventListener('click', quizBack);
  const retakeBtn = document.getElementById('quizRetakeBtn');
  if (retakeBtn && typeof resetQuiz === 'function') retakeBtn.addEventListener('click', resetQuiz);
}

// MOBILE MENU
function toggleMobileMenu() {
  document.getElementById('mobile-drawer').classList.toggle('is-open');
}

// CART DRAWER
function toggleCart() {
  document.getElementById('cartDrawer').classList.toggle('is-open');
  document.getElementById('cartOverlay').classList.toggle('is-visible');
  document.body.classList.toggle('has-drawer-open');
  // W2-4: keep the sticky mobile ATC hidden while the drawer is open.
  kbdSyncStickyAtc();
}

// ═══════════════════════════════════════════════════════════════════
// W2-4 (spec 2b): STICKY MOBILE ADD-TO-CART
// The bar is CSS-gated to ≤700px and only ever shows when the main
// #pdp-cta has scrolled out of view AND the cart drawer is closed. Its
// button mirrors the main CTA — it delegates its click to #pdp-cta and its
// variant id / price / disabled state are synced by kbdPdpUpdateVariant().
// ═══════════════════════════════════════════════════════════════════
let KBD_STICKY_CTA_VISIBLE = true; // whether the main #pdp-cta is currently in view

function kbdSyncStickyAtc() {
  const bar = document.getElementById('pdp-sticky-atc');
  if (!bar) return;
  const drawer = document.getElementById('cartDrawer');
  const drawerOpen = !!(drawer && drawer.classList.contains('is-open'));
  const show = !KBD_STICKY_CTA_VISIBLE && !drawerOpen;
  bar.classList.toggle('is-visible', show);
  bar.setAttribute('aria-hidden', show ? 'false' : 'true');
  document.body.classList.toggle('pdp-sticky-atc-active', show);
}

function initPdpStickyAtc() {
  const bar = document.getElementById('pdp-sticky-atc');
  const cta = document.getElementById('pdp-cta');
  if (!bar || !cta) return;

  // Sticky button click mirrors the main CTA — reuse its handler/cart logic.
  const stickyBtn = document.getElementById('pdp-sticky-cta');
  if (stickyBtn && stickyBtn.dataset.kbdBound !== '1') {
    stickyBtn.dataset.kbdBound = '1';
    stickyBtn.addEventListener('click', function () {
      const main = document.getElementById('pdp-cta');
      if (main && !main.disabled) main.click();
    });
  }

  // Re-observe per PDP render (Swup swaps the DOM); guard on the fresh element.
  if (bar.dataset.kbdObserved === '1') return;
  bar.dataset.kbdObserved = '1';
  KBD_STICKY_CTA_VISIBLE = true;
  if (typeof IntersectionObserver === 'undefined') return;
  const io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { KBD_STICKY_CTA_VISIBLE = e.isIntersecting; });
    kbdSyncStickyAtc();
  }, { threshold: 0 });
  io.observe(cta);
}

function toggleAccountDrawer() {
  document.getElementById('accountDrawer').classList.toggle('is-open');
  document.getElementById('accountOverlay').classList.toggle('is-visible');
  document.body.classList.toggle('has-drawer-open');
}

// AGE NOTICE
function dismissAgeNotice() {
  document.getElementById('ageNotice').classList.remove('is-visible');
  try { localStorage.setItem('kbd_age_confirmed', '1'); } catch(e) {}
}
// Show age notice on first visit (idempotent via body flag so Swup reinit won't
// stack multiple timers).
function initAgeNoticeTimer() {
  if (document.body.dataset.kbdAgeTimerBound === '1') return;
  document.body.dataset.kbdAgeTimerBound = '1';
  setTimeout(() => {
    try {
      if (!localStorage.getItem('kbd_age_confirmed')) {
        const el = document.getElementById('ageNotice');
        if (el) el.classList.add('is-visible');
      }
    } catch(e) {
      const el = document.getElementById('ageNotice');
      if (el) el.classList.add('is-visible');
    }
  }, 1500);
}

// COLOR SWATCH SELECTION (PDP + filters) — delegated to document so it survives
// Swup content swaps without rebinding. Guarded against double-binding.
function initColorSwatchDelegation() {
  if (document.body.dataset.kbdSwatchDelegated === '1') return;
  document.body.dataset.kbdSwatchDelegated = '1';
  document.addEventListener('click', (e) => {
    if (e.target.classList.contains('color-swatch')) {
      const group = e.target.parentElement;
      if (group.classList.contains('color-swatches')) {
        // PDP swatches (in pdp__selector) — exclusive; sidebar filter
        // swatches are now managed entirely by the IIFE inside
        // sections/collection.liquid, so this delegate must NOT touch
        // their is-selected state. The previous unconditional toggle
        // here ran AFTER the IIFE handler and inverted whatever the
        // IIFE had just set — hence the user-visible "ring appears on
        // deactivate" behavior. Limit this delegate to PDP only.
        const inPdp = group.closest('.pdp__selector');
        if (inPdp) {
          group.querySelectorAll('.color-swatch').forEach(s => s.classList.remove('is-selected'));
          e.target.classList.add('is-selected');
          const label = inPdp.querySelector('.pdp__selector-label span');
          // Color label — guarded with the data-color presence check so the
          // PDP markup that doesn't carry data-color (legacy templates) won't
          // throw on click. New product.liquid sets data-color from variant.title.
          const color = e.target.dataset.color;
          if (label && color) label.textContent = color.charAt(0).toUpperCase() + color.slice(1);
          // Swap the gallery to this variant's image set. The thumb DOM is
          // replaced in place; initPdpGallery's delegated thumb click handler
          // keeps working without rebinding.
          const variantImages = e.target.getAttribute('data-variant-images');
          if (variantImages) {
            kbdRebuildPdpGallery(variantImages);
            // W3-4: colour change shows a different image. Drop the LCP srcset
            // (pins the initial variant's first image) so src wins from here on.
            var _pdpMain = document.getElementById('pdp-main-image');
            if (_pdpMain) _pdpMain.removeAttribute('srcset');
            // Belt-and-braces: explicitly force the main hero image to the
            // first image of the new variant's gallery. kbdRebuildPdpGallery
            // already does this, but on some PDPs (notably the jacket) the
            // main image was sticking on the previous variant — likely a
            // browser cache / paint-order quirk. Re-setting src here, after
            // the thumb DOM is in place, makes the swap deterministic.
            try {
              const parsed = JSON.parse(variantImages);
              if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].main) {
                const mainImg = document.getElementById('pdp-main-image');
                if (mainImg && mainImg.getAttribute('src') !== parsed[0].main) {
                  mainImg.setAttribute('src', parsed[0].main);
                }
              }
            } catch (err) { /* JSON parse failure — silently skip */ }
          }
          // W2-6: do NOT auto-switch the size — a silent swap adds the wrong
          // item (audit A-3). kbdPdpUpdateVariant() strictly resolves the combo
          // and surfaces an "unavailable" message when it has no variant.
          kbdPdpUpdateVariant();
        }
      }
    }
    // Size selector — delegated so it works regardless of when the DOM renders
    if (e.target.classList.contains('pdp__size-btn')) {
      const sel = e.target.closest('#pdp-size-selector');
      if (sel) {
        sel.querySelectorAll('.pdp__size-btn').forEach(b => b.classList.remove('is-selected'));
        e.target.classList.add('is-selected');
        // W2-6: do NOT auto-switch the colour — a silent swap adds the wrong
        // item (audit A-3). kbdPdpUpdateVariant() strictly resolves the combo
        // and surfaces an "unavailable" message when it has no variant.
        kbdPdpUpdateVariant();
      }
    }
  });
}

// Syncs the gallery to whichever color swatch is currently is-selected.
// Called on init so the gallery always matches the Liquid smart-default swatch,
// even if Liquid built the gallery for a different variant's images.
function kbdPdpSmartDefault() {
  const selectedSwatch = document.querySelector('#pdp-swatches .color-swatch.is-selected');
  if (!selectedSwatch) return;
  const variantImages = selectedSwatch.getAttribute('data-variant-images');
  if (!variantImages) return;
  kbdRebuildPdpGallery(variantImages);
  try {
    const parsed = JSON.parse(variantImages);
    if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].main) {
      const mainImg = document.getElementById('pdp-main-image');
      if (mainImg) mainImg.setAttribute('src', parsed[0].main);
    }
  } catch (err) {}
}

// Resolves the correct variant from the JSON block Liquid writes into the page,
// then keeps the CTA button, section data-variant-id, price, and size-button
// availability all in sync. Called whenever color or size selection changes.
function kbdPdpUpdateVariant() {
  const jsonEl = document.getElementById('pdp-variants-json');
  if (!jsonEl) return;
  let variants;
  try { variants = JSON.parse(jsonEl.textContent); } catch (e) { return; }
  if (!Array.isArray(variants) || !variants.length) return;

  const colorSwatch = document.querySelector('#pdp-swatches .color-swatch.is-selected');
  const colorValue  = colorSwatch ? colorSwatch.getAttribute('data-color-value') : null;
  const sizeBtn     = document.querySelector('#pdp-size-selector .pdp__size-btn.is-selected');
  const sizeValue   = sizeBtn ? sizeBtn.getAttribute('data-size-value') : null;

  // W2-6: STRICT resolution — resolve ONLY against the selected option values.
  // Never loosen to a different variant; a silent swap adds the wrong item
  // (audit A-3). A null result means the selected combo has no variant at all.
  let resolved = null;
  if (colorValue && sizeValue) {
    resolved = variants.find(v => v.options.includes(colorValue) && v.options.includes(sizeValue)) || null;
  } else if (colorValue) {
    resolved = variants.find(v => v.options.includes(colorValue)) || null;
  } else if (sizeValue) {
    resolved = variants.find(v => v.options.includes(sizeValue)) || null;
  } else {
    // Single-variant / optionless product: preserve prior default resolution.
    resolved = variants.find(v => v.available) || variants[0];
  }

  const pdpSection  = document.querySelector('[data-page="pdp"]');
  const cta         = document.getElementById('pdp-cta');
  const stickyBtn   = document.getElementById('pdp-sticky-cta');
  const stickyPrice = document.getElementById('pdp-sticky-price');
  const comboMsg    = document.getElementById('pdp-combo-msg');
  const priceEl     = document.getElementById('pdp-price');
  const stockBanner = document.getElementById('pdp-stock-banner');

  // Cross-mark option buttons against the CURRENTLY selected other option so
  // dead-end combos read as unavailable. Size buttons stay disabled (existing
  // behavior); colour swatches are only muted so they remain clickable to
  // switch context. Runs regardless of whether the combo itself resolves.
  document.querySelectorAll('#pdp-size-selector .pdp__size-btn').forEach(btn => {
    const sv = btn.getAttribute('data-size-value');
    const avail = colorValue
      ? variants.some(v => v.options.includes(colorValue) && v.options.includes(sv) && v.available)
      : variants.some(v => v.options.includes(sv) && v.available);
    btn.disabled = !avail;
    btn.classList.toggle('is-unavailable', !avail);
  });
  document.querySelectorAll('#pdp-swatches .color-swatch').forEach(sw => {
    const cv = sw.getAttribute('data-color-value');
    const avail = sizeValue
      ? variants.some(v => v.options.includes(cv) && v.options.includes(sizeValue) && v.available)
      : variants.some(v => v.options.includes(cv) && v.available);
    sw.classList.toggle('is-unavailable', !avail);
  });

  // No variant exists for the selected combo → disable ATC (main + sticky),
  // show a clear message, and NEVER point the CTA at a different variant.
  if (!resolved) {
    if (pdpSection) pdpSection.setAttribute('data-variant-id', '');
    if (cta) {
      cta.setAttribute('data-variant-id', '');
      cta.disabled = true;
      cta.textContent = 'Unavailable';
    }
    if (stickyBtn) {
      stickyBtn.setAttribute('data-variant-id', '');
      stickyBtn.disabled = true;
      stickyBtn.textContent = 'Unavailable';
    }
    if (comboMsg) {
      comboMsg.textContent = sizeValue
        ? ("This colour isn't available in " + sizeValue + '.')
        : "This combination isn't available.";
      comboMsg.style.display = '';
    }
    if (stockBanner) {
      stockBanner.className = 'pdp__stock-banner';
      stockBanner.textContent = '';
      stockBanner.style.display = 'none';
    }
    return;
  }

  // A variant exists for this combo — clear any prior unavailable message.
  if (comboMsg) { comboMsg.textContent = ''; comboMsg.style.display = 'none'; }

  // Sync section + CTA variant ID
  if (pdpSection) pdpSection.setAttribute('data-variant-id', String(resolved.id));
  if (cta) {
    cta.setAttribute('data-variant-id', String(resolved.id));
    if (resolved.available) {
      cta.disabled = false;
      cta.textContent = 'Add to Cart';
    } else {
      cta.disabled = true;
      cta.textContent = 'Sold out';
    }
  }

  // W2-4: mirror variant id, price, and disabled/sold-out state onto the
  // sticky mobile ATC button so it always matches the main CTA.
  if (stickyBtn) {
    stickyBtn.setAttribute('data-variant-id', String(resolved.id));
    stickyBtn.disabled = cta ? cta.disabled : !resolved.available;
    stickyBtn.textContent = resolved.available ? 'Add to Cart' : 'Sold out';
  }
  if (stickyPrice) stickyPrice.textContent = kbdFormatPrice(resolved.price / 100);

  // Sync price display
  if (priceEl) priceEl.textContent = kbdFormatPrice(resolved.price / 100);

  // Low-stock banner (spec 2c) — smoke/muted warning treatment, never the
  // primary red. Only when inventory is actually tracked, the variant is
  // available, and 0 < qty <= 75. Updates on every variant change.
  if (stockBanner) {
    const invQty = resolved.inventory_quantity;
    const tracked = !!resolved.inventory_management;
    if (resolved.available && tracked && Number.isInteger(invQty) && invQty > 0 && invQty <= 75) {
      stockBanner.className = 'pdp__stock-banner pdp__stock-banner--low';
      stockBanner.textContent = 'Only ' + invQty + ' left in this colour.';
      stockBanner.style.display = '';
    } else {
      stockBanner.className = 'pdp__stock-banner';
      stockBanner.textContent = '';
      stockBanner.style.display = 'none';
    }
  }

  kbdPdpSyncInventory(resolved.inventory_quantity);
}

// Syncs the quantity selector's max cap, + button disabled state, and low-stock
// notice to match the resolved variant's inventory_quantity. Called from
// kbdPdpUpdateVariant() on every variant change and on initial page load.
function kbdPdpSyncInventory(invQty) {
  const wrap = document.getElementById('pdp-qty-wrap');
  if (!wrap) return;
  wrap.setAttribute('data-max-qty', String(invQty));

  const valEl  = wrap.querySelector('.pdp__qty-val');
  const incBtn = document.getElementById('pdp-qty-inc');
  const notice = document.getElementById('pdp-qty-notice');

  // Only cap when Shopify is tracking positive inventory
  const hasLimit = Number.isInteger(invQty) && invQty > 0;
  const max = hasLimit ? invQty : Infinity;

  if (valEl) {
    if (hasLimit) { valEl.setAttribute('max', String(max)); }
    else { valEl.removeAttribute('max'); }
  }

  if (hasLimit && valEl) {
    const cur = parseInt(valEl.value, 10) || 1;
    if (cur > max) valEl.value = String(max);
  }

  if (incBtn && valEl) {
    const cur = parseInt(valEl.value, 10) || 1;
    incBtn.disabled = hasLimit && cur >= max;
  }

  if (notice) {
    if (invQty >= 1 && invQty <= 5) {
      notice.textContent = 'Only ' + invQty + ' left';
      notice.style.display = '';
    } else {
      notice.textContent = '';
      notice.style.display = 'none';
    }
  }
}

// Delegated quantity button handler — wired once at document level so it
// survives Swup content swaps without rebinding.
function initPdpQtyDelegation() {
  if (document.body.dataset.kbdQtyDelegated === '1') return;
  document.body.dataset.kbdQtyDelegated = '1';

  function getMax(wrap) {
    const raw = wrap ? parseInt(wrap.getAttribute('data-max-qty'), 10) : NaN;
    return !isNaN(raw) && raw > 0 ? raw : Infinity;
  }

  // +/− button clicks
  document.addEventListener('click', (e) => {
    const isDec = e.target.closest('#pdp-qty-dec');
    const isInc = e.target.closest('#pdp-qty-inc');
    if (!isDec && !isInc) return;

    const wrap   = document.getElementById('pdp-qty-wrap');
    const valEl  = wrap ? wrap.querySelector('.pdp__qty-val') : null;
    const incBtn = document.getElementById('pdp-qty-inc');
    if (!valEl) return;

    const max = getMax(wrap);
    let cur = parseInt(valEl.value, 10) || 1;
    cur = isDec ? Math.max(1, cur - 1) : (max === Infinity ? cur + 1 : Math.min(max, cur + 1));
    valEl.value = String(cur);
    if (incBtn) incBtn.disabled = max !== Infinity && cur >= max;
  });

  // Live: update + button state while typing
  document.addEventListener('input', (e) => {
    if (!e.target.classList.contains('pdp__qty-val')) return;
    const wrap   = document.getElementById('pdp-qty-wrap');
    const incBtn = document.getElementById('pdp-qty-inc');
    const max    = getMax(wrap);
    const val    = parseInt(e.target.value, 10);
    if (incBtn) incBtn.disabled = max !== Infinity && !isNaN(val) && val >= max;
  });

  // Commit: clamp to [1, max] when field loses focus or Enter is pressed
  function clampQtyInput(input) {
    const wrap   = document.getElementById('pdp-qty-wrap');
    const incBtn = document.getElementById('pdp-qty-inc');
    const max    = getMax(wrap);
    let val = parseInt(input.value, 10);
    if (isNaN(val) || val < 1) val = 1;
    if (max !== Infinity && val > max) val = max;
    input.value = String(val);
    if (incBtn) incBtn.disabled = max !== Infinity && val >= max;
  }

  document.addEventListener('change', (e) => {
    if (e.target.classList.contains('pdp__qty-val')) clampQtyInput(e.target);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && e.target.classList.contains('pdp__qty-val')) {
      clampQtyInput(e.target);
      e.target.blur();
    }
  });
}

// FILTER PILLS (Journal) — rebound on every page:view since the pills sit
// inside #swup and are replaced on each soft navigation. Per-element guard
// prevents double-binding if init is called twice on the same DOM.
function initFilterPills() {
  document.querySelectorAll('.filter-pill').forEach(pill => {
    if (pill.dataset.kbdInit === '1') return;
    pill.dataset.kbdInit = '1';
    pill.addEventListener('click', () => {
      pill.parentElement.querySelectorAll('.filter-pill').forEach(p => p.classList.remove('is-active'));
      pill.classList.add('is-active');
    });
  });
}

// QUIZ — data (quizQuestions, quizState) moved above the throwing render
// calls so it initializes before they can halt execution. Function
// declarations stay here.
function renderQuizStep() {
  const q = quizQuestions[quizState.currentStep];
  document.getElementById('quizQuestion').textContent = q.question;
  const optsEl = document.getElementById('quizOptions');
  optsEl.innerHTML = '';
  q.options.forEach(opt => {
    const btn = document.createElement('button');
    btn.className = 'quiz__option';
    btn.textContent = opt.label;
    btn.onclick = () => selectQuizOption(btn, opt.value);
    // Restore previous selection if revisiting
    if (quizState.answers[quizState.currentStep] === opt.value) {
      btn.classList.add('is-selected');
    }
    optsEl.appendChild(btn);
  });
  // Progress bar
  const progress = ((quizState.currentStep + 1) / quizQuestions.length) * 100;
  document.getElementById('quizProgress').style.width = progress + '%';
  document.getElementById('quizProgressLabel').textContent = `Question ${quizState.currentStep + 1} of ${quizQuestions.length}`;
  // Back button
  const back = document.querySelector('.quiz__back');
  if (quizState.currentStep === 0) back.classList.add('is-hidden');
  else back.classList.remove('is-hidden');
  // Next button state
  const next = document.getElementById('quizNext');
  if (quizState.answers[quizState.currentStep]) {
    next.disabled = false;
    next.style.opacity = '1';
  } else {
    next.disabled = true;
    next.style.opacity = '0.4';
  }
  // Button label on last step
  next.textContent = quizState.currentStep === quizQuestions.length - 1 ? 'See my result →' : 'Next →';
}

function selectQuizOption(btn, value) {
  btn.parentElement.querySelectorAll('.quiz__option').forEach(o => o.classList.remove('is-selected'));
  btn.classList.add('is-selected');
  quizState.answers[quizState.currentStep] = value;
  document.getElementById('quizNext').disabled = false;
  document.getElementById('quizNext').style.opacity = '1';
}

// Slide the quiz step out left, then re-render and slide in from right.
function kbdAnimateQuizStep(rerender) {
  const step = document.getElementById('quizStep');
  if (!step) { rerender(); return; }
  step.classList.remove('is-entering-right');
  step.classList.add('is-leaving-left');
  setTimeout(() => {
    step.classList.remove('is-leaving-left');
    rerender();
    step.classList.add('is-entering-right');
    setTimeout(() => step.classList.remove('is-entering-right'), 220);
  }, 180);
}

function quizNext() {
  if (!quizState.answers[quizState.currentStep]) return;
  if (quizState.currentStep < quizQuestions.length - 1) {
    quizState.currentStep++;
    kbdAnimateQuizStep(renderQuizStep);
  } else {
    // Show results
    populateQuizResult(quizState.answers);
    document.getElementById('quizStep').style.display = 'none';
    document.querySelector('.quiz__progress').style.display = 'none';
    document.querySelector('.quiz').style.display = 'none';
    document.getElementById('quizResult').style.display = 'block';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

function quizBack() {
  if (quizState.currentStep > 0) {
    quizState.currentStep--;
    kbdAnimateQuizStep(renderQuizStep);
  }
}

function resetQuiz() {
  quizState = { currentStep: 0, answers: [] };
  document.getElementById('quizStep').style.display = 'block';
  document.querySelector('.quiz__progress').style.display = 'block';
  document.querySelector('.quiz').style.display = 'block';
  document.getElementById('quizResult').style.display = 'none';
  renderQuizStep();
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

// Initial renderQuizStep() call now lives inside the setupQuiz IIFE earlier
// in the file, which gates on DOMContentLoaded and on the quiz DOM existing.

// PDP thumbnail clicks are bound per-thumb inside renderPDP()

// PREVENT default link behavior for all navigation anchors not handled.
// Rebound on every page:view since anchors sit inside #swup.
function initAnchorPreventDefault() {
  document.querySelectorAll('a[href="#"]').forEach(a => {
    if (a.dataset.kbdInit === '1') return;
    a.dataset.kbdInit = '1';
    if (!a.onclick) a.addEventListener('click', e => e.preventDefault());
  });
}

// JOURNAL CATEGORY FILTER — pill buttons filter the article grid by
// data-category. "All" pill clears the filter. Empty state shows when
// no articles match (none currently empty, but future-proofed).
function initJournalFilter() {
  const journalPage = document.querySelector('[data-page="journal"]');
  if (!journalPage || journalPage.dataset.kbdInit === '1') return;
  journalPage.dataset.kbdInit = '1';
  const pills = journalPage.querySelectorAll('.filter-pill[data-category]');
  const cards = journalPage.querySelectorAll('.journal-card[data-category]');
  const empty = journalPage.querySelector('#journal-empty');
  const loadMore = journalPage.querySelector('#journal-load-more');

  // Load-more has no real pagination wired up on the Shopify side — blog.liquid
  // renders every blog.articles entry in a single pass. Hide the button whenever
  // all matching cards for the current category are already in the DOM and
  // visible. Scoped as its own function so it can run even on pages with no
  // pills or no cards (still hides the dangling button).
  function hideLoadMoreIfComplete(cat) {
    if (!loadMore) return;
    let total = 0, hiddenByPagination = 0;
    cards.forEach(c => {
      const match = cat === 'all' || c.getAttribute('data-category') === cat;
      if (match) {
        total++;
        // 'display:none' inline or a pagination class would signal "not yet
        // loaded." The filter uses .filtered-out (visibility driven), which
        // doesn't count as hidden-by-pagination.
        if (c.style.display === 'none') hiddenByPagination++;
      }
    });
    loadMore.style.display = (total > 0 && hiddenByPagination > 0) ? '' : 'none';
  }

  if (!pills.length || !cards.length) {
    // Still hide the load-more button even if there's nothing to filter.
    hideLoadMoreIfComplete('all');
    return;
  }

  pills.forEach(pill => {
    pill.addEventListener('click', () => {
      const cat = pill.getAttribute('data-category');
      pills.forEach(p => p.classList.toggle('is-active', p === pill));
      let visible = 0;
      cards.forEach(c => {
        const match = cat === 'all' || c.getAttribute('data-category') === cat;
        // Shop-page convention: .filtered-out drives visibility via CSS.
        c.classList.toggle('filtered-out', !match);
        if (match) visible++;
      });
      if (empty) empty.hidden = visible > 0;
      hideLoadMoreIfComplete(cat);
    });
  });

  hideLoadMoreIfComplete('all');
}

// MEGA MENU — open/close handled by CSS :hover. JS only forces the panel
// closed on click of the trigger link or on Swup's link:click hook for any
// link inside the panel, then clears the override on the next mouseleave
// so hover works again. The header sits outside #swup, so we only bind
// once.
//
// We deliberately do NOT bind a synchronous click listener on the panel
// itself: doing so toggles .is-closing (visibility:hidden / pointer-events:
// none) right as the click bubbles up to Swup's document-level delegate,
// which on some browsers caused Swup to drop the click and fall back to a
// hard-navigation white flash. Closing via swup.hooks.on('link:click', …)
// inside kbdInitSwup runs AFTER Swup has accepted the click, so the link is
// always intercepted cleanly while the menu still fades out alongside the
// page transition.
function initMegaMenuClose() {
  document.querySelectorAll('.header__nav-item--has-mega').forEach(item => {
    if (item.dataset.kbdInit === '1') return;
    item.dataset.kbdInit = '1';
    const close = () => item.classList.add('is-closing');
    const reset = () => item.classList.remove('is-closing');
    item.addEventListener('mouseleave', reset);
    const trigger = item.querySelector(':scope > a');
    if (trigger) trigger.addEventListener('click', close);
  });
}

// Force any open mega panel closed at navigation time. Called from the
// Swup link:click hook (real soft-nav) and exposed for the no-Swup fallback
// path inside the trigger click handler above.
//
// Also blurs any focused element inside a mega item: the header sits
// outside #swup, so a link the user just clicked retains focus across
// soft-nav. CSS opens the panel on :focus-within, so the moment the user
// moves their mouse away (mouseleave drops .is-closing), :focus-within
// would re-open the panel without a fresh hover. Blurring here neutralizes
// that for every link click, not just collection-bound ones (the cooldown
// path already handles this for /collections/*).
//
// Sets kbdMegaJustClosed so the Swup visit:start hook can pause for the
// close transition to complete before the page fade-out starts.
let kbdMegaJustClosed = false;
function closeAllMegaMenus() {
  const focused = document.activeElement;
  let anyOpen = false;
  document.querySelectorAll('.header__nav-item--has-mega').forEach(item => {
    // Track whether the panel was actually visible (i.e. CSS would have it
    // open via :hover / :focus-within / .is-open). If nothing was open,
    // there's no fade to wait for, so we can skip the visit:start delay.
    if (
      item.matches(':hover') ||
      item.matches(':focus-within') ||
      item.classList.contains('is-open')
    ) {
      anyOpen = true;
    }
    item.classList.add('is-closing');
    if (focused && focused !== document.body && item.contains(focused)) {
      try { focused.blur(); } catch (e) {}
    }
  });
  if (anyOpen) kbdMegaJustClosed = true;
}

// Re-execute inline <script> tags that were swapped into #swup by Swup's
// content:replace step. Browsers don't run scripts inserted via innerHTML,
// so section-level scripts (e.g. the filter IIFE inside collection.liquid's
// <script> block) silently fail to wire up on soft-navigation. Cloning the
// element and replacing it triggers normal script execution.
//
// Scoped to #swup only so we never touch persistent scripts in <head> /
// header / footer that already ran on the initial page load.
function kbdReExecuteSwupScripts() {
  const root = document.getElementById('swup');
  if (!root) return;
  root.querySelectorAll('script').forEach(oldScript => {
    const newScript = document.createElement('script');
    Array.from(oldScript.attributes).forEach(attr => {
      newScript.setAttribute(attr.name, attr.value);
    });
    newScript.textContent = oldScript.textContent;
    oldScript.parentNode.replaceChild(newScript, oldScript);
  });
}

// MEGA MENU LANDING-COOLDOWN — when the user clicks SHOP (or a link inside
// the SHOP mega panel) and lands on a collection page, two CSS triggers
// would otherwise re-open the panel without a fresh user gesture:
//   1. :hover — cursor often comes to rest on the SHOP nav link.
//   2. :focus-within — the header sits outside #swup, so the clicked link
//      retains focus across the soft navigation, keeping the panel open.
// We add .is-cooldown for 1500ms (CSS forces the panel hidden, peer of
// .is-closing) AND blur any focused element inside a mega item so the
// :focus-within trigger goes false. Then we attach a one-shot mouseleave
// handler per item: leaving SHOP at any point during the cooldown cancels
// the timer and immediately drops .is-cooldown — so the panel never opens
// "automatically" at expiry; it only opens when the user is actively
// hovering when the timer fires (or hovers fresh afterwards).
let kbdMegaCooldownTimer = null;
let kbdMegaCooldownLeaveHandlers = [];
function applyMegaMenuLandingCooldown() {
  const items = document.querySelectorAll('.header__nav-item--has-mega');
  if (!items.length) return;

  // Always clear prior state first so navigating away from a collection
  // page mid-cooldown unblocks the menu immediately.
  if (kbdMegaCooldownTimer) {
    clearTimeout(kbdMegaCooldownTimer);
    kbdMegaCooldownTimer = null;
  }
  kbdMegaCooldownLeaveHandlers.forEach(({ item, handler }) => {
    item.removeEventListener('mouseleave', handler);
  });
  kbdMegaCooldownLeaveHandlers = [];
  items.forEach(item => item.classList.remove('is-cooldown'));

  if (window.location.pathname.indexOf('/collections/') !== 0) return;

  // Drop any focus that's currently inside a mega item so :focus-within
  // can't re-open the panel as soon as .is-cooldown is removed.
  const focused = document.activeElement;
  if (focused && focused !== document.body) {
    items.forEach(item => {
      if (item.contains(focused)) {
        try { focused.blur(); } catch (e) {}
      }
    });
  }

  // Apply cooldown.
  items.forEach(item => item.classList.add('is-cooldown'));

  // Per-item mouseleave during cooldown: cancel timer + drop class right
  // away. Avoids the auto-open at expiry the user reported.
  items.forEach(item => {
    const handler = () => {
      if (kbdMegaCooldownTimer) {
        clearTimeout(kbdMegaCooldownTimer);
        kbdMegaCooldownTimer = null;
      }
      items.forEach(i => i.classList.remove('is-cooldown'));
      kbdMegaCooldownLeaveHandlers.forEach(({ item: it, handler: h }) => {
        it.removeEventListener('mouseleave', h);
      });
      kbdMegaCooldownLeaveHandlers = [];
    };
    item.addEventListener('mouseleave', handler);
    kbdMegaCooldownLeaveHandlers.push({ item, handler });
  });

  // Timer expiry: drop the class. With :focus-within neutralized above,
  // whether the panel opens at this instant depends purely on :hover —
  // exactly what the user asked for.
  kbdMegaCooldownTimer = setTimeout(() => {
    items.forEach(item => item.classList.remove('is-cooldown'));
    kbdMegaCooldownLeaveHandlers.forEach(({ item, handler }) => {
      item.removeEventListener('mouseleave', handler);
    });
    kbdMegaCooldownLeaveHandlers = [];
    kbdMegaCooldownTimer = null;
  }, 1500);
}

// ═══════════════════════════════════════════════════════════════════
// BOOTSTRAP + SWUP INITIALIZATION
// ═══════════════════════════════════════════════════════════════════
// Two groups of init functions:
//   • kbdInitOnce() — things that bind to document/body/window or to
//     persistent elements outside the #swup container (header, drawers,
//     search overlay). Safe to call more than once
//     because each has a dataset flag to short-circuit.
//   • kbdInitPage() — things inside #swup that get replaced on every
//     Swup soft navigation, so they need to be reinitialized after
//     each page:view. Each is idempotent against its own target DOM.
//
// page:view fires on initial load too, so kbdInitPage runs via Swup on
// first paint when Swup is available. When Swup is skipped (design mode
// or CDN unavailable), kbdBootstrap calls kbdInitPage directly.
// ═══════════════════════════════════════════════════════════════════

function initFooterAccordion() {
  document.addEventListener('click', (e) => {
    if (!window.matchMedia('(max-width: 768px)').matches) return;
    const title = e.target.closest('.footer__col-title');
    if (!title) return;
    const col = title.closest('.footer__col--nav');
    if (col) col.classList.toggle('is-open');
  });
}

function kbdInitOnce() {
  initNavTransparency();
  initSearchOverlay();
  initMegaMenuClose();
  initCartWiring();
  initColorSwatchDelegation();
  initPdpQtyDelegation();
  initAgeNoticeTimer();
  initFooterAccordion();
}

// PDP — when the user arrives with a `?color=<Label>` query param (set on
// the collection page when a color filter is active), find the matching
// swatch and trigger its click so the gallery, main image, and COLOR
// label all update to that variant. Falls back silently to the default
// (Liquid-rendered) selection when the param is missing or no swatch
// matches. Slugifies the param the same way Liquid's `handle` filter does
// to align with each swatch's data-color attribute.
function kbdApplyPdpColorFromUrl() {
  const swatches = document.querySelectorAll('.pdp__selector .color-swatch');
  if (!swatches.length) return;
  let raw;
  try { raw = new URLSearchParams(window.location.search).get('color'); } catch (e) { return; }
  if (!raw) return;
  const slug = String(raw).toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (!slug) return;
  let target = null;
  for (let i = 0; i < swatches.length; i++) {
    if (swatches[i].getAttribute('data-color') === slug) { target = swatches[i]; break; }
  }
  if (!target) return;
  if (target.classList.contains('is-selected')) return; // already on it
  // Defer one tick so initPdpGallery's ensureMainImageSrc runs first; then
  // the click delegate (initColorSwatchDelegation) handles is-selected,
  // label text, and kbdRebuildPdpGallery for this variant's images.
  setTimeout(() => target.click(), 0);
}

function kbdNewsletterInit() {
  if (!window.location.search.includes('newsletter=subscribed')) return;
  const successHtml = '<strong>Welcome.</strong> You\'ll hear from us soon.';
  const homepageForm = document.querySelector('.newsletter__form');
  if (homepageForm) {
    const wrap = document.createElement('div');
    wrap.className = 'newsletter__confirm';
    wrap.innerHTML = successHtml;
    homepageForm.replaceWith(wrap);
  }
  const footerForm = document.querySelector('.footer-newsletter__form');
  if (footerForm) {
    const wrap = document.createElement('div');
    wrap.className = 'newsletter__confirm';
    wrap.style.cssText = 'border-color: rgba(255,255,255,0.25);';
    wrap.innerHTML = successHtml;
    footerForm.replaceWith(wrap);
  }
  history.replaceState(null, '', window.location.pathname);
}

function kbdInitPage() {
  updateNavTransparentByUrl();
  applyMegaMenuLandingCooldown();
  initFaqAccordion();
  initPdpGallery();
  kbdPdpSmartDefault();
  kbdPdpUpdateVariant();
  initPdpStickyAtc();
  kbdApplyPdpColorFromUrl();
  initQuiz();
  initFilters();
  initSort();
  initJournalFilter();
  initFilterPills();
  initAnchorPreventDefault();
  kbdNewsletterInit();
  // kbdInitPriceSlider lives further up; safely a no-op when its sidebar is absent.
  if (typeof kbdInitPriceSlider === 'function') {
    try { kbdInitPriceSlider(); } catch (e) {}
  }
  // Refresh the cart drawer/badge on navigation so per-page state stays in sync.
  if (typeof kbdCartFetch === 'function') {
    try { kbdCartFetch(); } catch (e) {}
  }
  // Render landing state for the search page when it's on-screen.
  if (typeof kbdRunSearch === 'function') {
    try { kbdRunSearch(); } catch (e) {}
  }
  // Render order details on the thank-you page if present.
  if (typeof kbdRenderThankYou === 'function') {
    try { kbdRenderThankYou(); } catch (e) {}
  }
}

function kbdInitSwup() {
  // Theme editor guard — Shopify's live preview needs section re-renders that
  // Swup would otherwise intercept, breaking the editor. Fall back to normal
  // navigation when the admin is previewing the theme.
  if (window.Shopify && window.Shopify.designMode) return null;
  if (typeof window.Swup === 'undefined') return null;
  const swup = new window.Swup({
    containers: ['#swup'],
    animationSelector: '[class*="transition-"]',
    linkSelector: 'a[href^="/"]:not([data-no-swup]):not([target="_blank"]):not([href^="/checkout"]):not([href^="/cart"])'
  });
  window.kbdSwup = swup; // kbd-motion.js re-inits reveals/rails/header state on content:replace + page:view (squarespace-look spec 4)
  // Close any open mega panel synchronously when Swup accepts a link click.
  // Fires after Swup has confirmed it will intercept (so it can't race the
  // navigation) but before the actual fetch/swap.
  swup.hooks.on('link:click', () => closeAllMegaMenus());
  // Pause the visit until the mega close transition (150ms, matched in
  // theme.css) has completed, so the panel fade and page fade run
  // sequentially instead of overlapping. Async hook returns a Promise that
  // Swup awaits before continuing the visit lifecycle.
  swup.hooks.on('visit:start', async () => {
    if (kbdMegaJustClosed) {
      kbdMegaJustClosed = false;
      await new Promise(resolve => setTimeout(resolve, 150));
    }
  });
  // Re-execute inline <script> blocks inside the swapped #swup content.
  // Browsers ignore scripts inserted via innerHTML, so section IIFEs (e.g.
  // collection.liquid's filter logic) wouldn't run on soft-nav otherwise.
  swup.hooks.on('content:replace', () => kbdReExecuteSwupScripts());
  swup.hooks.on('page:view', () => {
    kbdInitPage();
    kbdTrackPageView();
  });
  return swup;
}

// Soft navigations swap #swup without re-running content_for_header, so the
// analytics snippets that fire on a full page load never run again. Re-fire
// a page_view on every Swup page:view so GA4 and Shopify's own analytics
// don't undercount soft navs. Every call is optional-chained/guarded so a
// missing library can never throw and break the navigation.
function kbdTrackPageView() {
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_location: location.href,
        page_title: document.title
      });
    }
  } catch (e) {}
  try {
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib
        && typeof window.ShopifyAnalytics.lib.page === 'function') {
      window.ShopifyAnalytics.lib.page(null, { title: document.title });
    }
  } catch (e) {}
}

function kbdBootstrap() {
  // Bind persistent listeners first so they're always live, even if a
  // downstream mock renderer throws.
  kbdInitOnce();

  // Per-page inits — these also run again inside the Swup page:view hook.
  kbdInitPage();

  // Swup last so the initial page:view fire (if any) happens after everything
  // else is wired. If Swup is unavailable (design mode / CDN blocked), links
  // fall back to full page loads and kbdInitPage has already run for this one.
  kbdInitSwup();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', kbdBootstrap);
} else {
  kbdBootstrap();
}

// Standalone PDP main-image safety net. Runs on DOMContentLoaded
// independently of kbdBootstrap/initPdpGallery so even if any throw above
// short-circuits the rest of init, the main product image still resolves.
// If the Liquid src is empty, we read the first thumb's data-src, then
// the data-fallback-src. Idempotent — only acts when src is missing.
function kbdEnsurePdpMainImageSrc() {
  const mainImg = document.getElementById('pdp-main-image');
  if (!mainImg) return;
  const cur = mainImg.getAttribute('src');
  if (cur && cur.length > 0) return;
  const firstThumb = document.querySelector('#pdp-thumbs .pdp__thumb');
  if (firstThumb) {
    const ds = firstThumb.getAttribute('data-src');
    if (ds) { mainImg.src = ds; return; }
    const fimg = firstThumb.querySelector('img');
    if (fimg && fimg.getAttribute('src')) { mainImg.src = fimg.getAttribute('src'); return; }
  }
  const fallback = mainImg.getAttribute('data-fallback-src');
  if (fallback) mainImg.src = fallback;
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', kbdEnsurePdpMainImageSrc);
} else {
  kbdEnsurePdpMainImageSrc();
}

